import { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Button,
  Typography,
  Paper,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Stack,
  Grid,
  Snackbar,
  Alert,
  Tooltip,
  Switch,
  FormControlLabel,
  InputAdornment,
  useMediaQuery,
  useTheme,
  Autocomplete,
  CircularProgress,
  Tabs,
  Tab,
  Chip,
  Avatar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  // CardMedia,
  // CardContent,
  // CardActions,
  // Container,
} from '@mui/material';
import { 
  DataGrid, 
  GridColDef, 
  GridRenderCellParams,
  GridColumnVisibilityModel
} from '@mui/x-data-grid';
import { DesignCutting as DesignCuttingType, DesignCuttingFormData } from '../types/designCutting';
import SearchIcon from '@mui/icons-material/Search';
import { designCuttingApi } from '../services/api';
import { Design, designApi } from '../api/design';
import AddIcon from '@mui/icons-material/Add';
import { debugComponentRendering, debugApiCall } from '../utils/debugHelper';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import NoImageIcon from '@mui/icons-material/ImageNotSupported';
import DispatchedDetailsDialog from '../components/DispatchedDetailsDialog';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

import DesignPreviewDialog from '../components/DesignPreviewDialog';
import { makeSafeGridColumns } from '../utils/dataGridHelpers';
import api, { shortfallApi } from '../services/api';
import { productionApi } from '../api/production';
import { useNavigate } from 'react-router-dom';
import StitchingCompletionDialog from '../components/StitchingCompletionDialog';
import { productionOrdersApi, ProductionOrder } from '../api/productionOrders';

interface DesignCuttingProps {
  canAdd: boolean;
  canEdit: boolean;
  canDelete: boolean;
}

const initialFormData: DesignCuttingFormData = {
  design_no: '',
  received_date: new Date().toISOString().split('T')[0],
  inner_received_date: undefined,
  garment_part: 'TOP',
  fabric_meter: 0,
  inner_fabric_meter: undefined,
  xs_pieces: 0,
  s_pieces: 0,
  m_pieces: 0,
  l_pieces: 0,
  xl_pieces: 0,
  xxl_pieces: 0,
  three_xl_pieces: 0,
  cutting_date: undefined,
  rate: undefined,
  notes: undefined,
  challan_date: new Date().toISOString().split('T')[0],
};

// Add helper function to detect mobile devices
const isMobileDevice = () => {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
};

export default function DesignCutting({ canAdd, canEdit, canDelete }: DesignCuttingProps) {
  // Add our rendering debugger
  const renderDebug = debugComponentRendering('DesignCutting');
  renderDebug.start();

  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();

  const [currentTab, setCurrentTab] = useState(0);
  const [pendingToReceive, setPendingToReceive] = useState<any[]>([]);
  const [completedDesigns, setCompletedDesigns] = useState<DesignCuttingType[]>([]);
  const [dispatchedDesigns, setDispatchedDesigns] = useState<DesignCuttingType[]>([]);
  const [designs, setDesigns] = useState<DesignCuttingType[]>([]);
  const [loading, setLoading] = useState(true);
  const [recordsWithAssignments, setRecordsWithAssignments] = useState<Set<number>>(new Set());
  const [receiveDialogOpen, setReceiveDialogOpen] = useState(false);
  const [selectedJobWork, setSelectedJobWork] = useState<any>(null);
  const [challanDate, setChallanDate] = useState(new Date().toISOString().split('T')[0]);
  const [stitchingDialogOpen, setStitchingDialogOpen] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [detailsDesignNo, setDetailsDesignNo] = useState<string | null>(null);
  const [inProcessSearch, setInProcessSearch] = useState('');
  const [dispatchedSearch, setDispatchedSearch] = useState('');
  const [selectedCuttingForCompletion, setSelectedCuttingForCompletion] = useState<any>(null);
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState<DesignCuttingFormData>(initialFormData);
  const [showTopSection, setShowTopSection] = useState(true);
  const [showBottomSection, setShowBottomSection] = useState(false);
  const [showJacketSection, setShowJacketSection] = useState(false);
  const [topFormData, setTopFormData] = useState<DesignCuttingFormData | null>(null);
  const [bottomFormData, setBottomFormData] = useState<DesignCuttingFormData | null>(null);
  const [jacketFormData, setJacketFormData] = useState<DesignCuttingFormData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [editMode, setEditMode] = useState(false);
  const [selectedTopId, setSelectedTopId] = useState<number | undefined>(undefined);
  const [selectedBottomId, setSelectedBottomId] = useState<number | undefined>(undefined);
  const [selectedJacketId, setSelectedJacketId] = useState<number | undefined>(undefined);
  const [currentBatchNumber, setCurrentBatchNumber] = useState<number | undefined>(undefined);
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredDesigns, setFilteredDesigns] = useState<DesignCuttingType[]>([]);
  const [apiError, setApiError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const [showPendingOnly, setShowPendingOnly] = useState(true);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [isMobile, setIsMobile] = useState(false);
  const [selectedPreviewDesignNo, setSelectedPreviewDesignNo] = useState('');
  const [selectedPreviewTopDesign, setSelectedPreviewTopDesign] = useState<DesignCuttingType | undefined>(undefined);
  const [selectedPreviewBottomDesign, setSelectedPreviewBottomDesign] = useState<DesignCuttingType | undefined>(undefined);
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  
  // Add state for designs from designApi
  const [designsList, setDesignsList] = useState<Design[]>([]);
  const [selectedDesign, setSelectedDesign] = useState<Design | null>(null);
  const [loadingDesigns, setLoadingDesigns] = useState(false);
  // Read-only flag for form dialog (used when opened from Stitching Complete)
  const [formReadOnly, setFormReadOnly] = useState(false);

  // Production Order state for prefilling
  const [productionOrders, setProductionOrders] = useState<ProductionOrder[]>([]);
  const [selectedPO, setSelectedPO] = useState<ProductionOrder | null>(null);
  const [loadingPOs, setLoadingPOs] = useState(false);

  const [columnVisibilityModel, setColumnVisibilityModel] = useState<GridColumnVisibilityModel>({
    design_no: true,
    image: true,
    total_pieces: true,
    received_date: true,
    inner_received_date: true,
    garment_part: true,
    fabric_meter: true,
    inner_fabric_meter: true,
    avg_fabric_per_piece: true,
    avg_inner_fabric_per_piece: true,
    xs_pieces: true,
    s_pieces: true,
    m_pieces: true,
    l_pieces: true,
    xl_pieces: true,
    xxl_pieces: true,
    three_xl_pieces: true,
    cutting_date: true,
    rate: true,
    status: true,
    actions: true
  });




  // Handle responsive column visibility
  useEffect(() => {
    const handleResize = () => {
      const isMobile = window.innerWidth < 600; // sm breakpoint
      setColumnVisibilityModel(prev => ({
        ...prev,
        avg_fabric_per_piece: !isMobile,
        avg_inner_fabric_per_piece: !isMobile
      }));
    };

    // Initial check
    handleResize();

    // Add event listener
    window.addEventListener('resize', handleResize);

    // Cleanup
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Add a useEffect to check for mobile devices
  useEffect(() => {
    setIsMobile(isMobileDevice());
  }, []);

  const columns: GridColDef[] = [
    { 
      field: 'design_no', 
      headerName: 'Design No', 
      flex: 1, 
      minWidth: 300,
      maxWidth: 350,
      hideable: false,
      renderCell: (params: GridRenderCellParams) => {
        // Add check for undefined params.row
        if (!params || !params.row) return null;
        
        // Get image URL from design relation or direct field
        const imageUrl = params.row.design?.image_url || 
                        params.row.design?.image2_url || 
                        params.row.design?.image3_url || 
                        params.row.design?.image4_url ||
                        params.row.image_url;
        
        return (
          <Stack direction="row" spacing={3} alignItems="center">
          <Box
            sx={{
                width: 150,
                height: 150,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'divider',
              overflow: 'hidden',
              bgcolor: 'background.default',
              cursor: imageUrl ? 'pointer' : 'default'
            }}
            onClick={() => {
              if (imageUrl) {
                // If status is PENDING, open edit form to enter cutting details
                if (params.row.status === 'PENDING') {
                  handleEdit(params.row);
                }
                // If status is COMPLETED, open stitching completion dialog to mark job work complete
                else if (params.row.status === 'COMPLETED') {
                  setSelectedCuttingForCompletion(params.row);
                  setStitchingDialogOpen(true);
                }
                // If status is RECEIVED, open stitching completion dialog
                else if (params.row.status === 'RECEIVED') {
                  setSelectedCuttingForCompletion(params.row);
                  setStitchingDialogOpen(true);
                }
                // Otherwise just preview the image
                else {
                  openImagePreview(imageUrl, params.row.design_no);
                }
              }
            }}
          >
            {imageUrl ? (
              <Box
                component="img"
                src={imageUrl}
                alt={`Design ${params.row.design_no}`}
                sx={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover'
                }}
              />
            ) : (
                <NoImageIcon sx={{ color: 'text.secondary', fontSize: 60 }} />
            )}
          </Box>
            <Typography variant="body1" fontWeight="medium">{params.row.design_no}</Typography>
          </Stack>
        );
      }
    },
    {
      field: 'batch_info',
      headerName: 'Batch',
      width: 100,
      renderCell: (params: any) => {
        if (!params || !params.row) return '-';
        
        const row = params.row as any;
        if (!row.batch_number) return '-';
        
        // Show the specific batch number for this row
        return `Batch ${row.batch_number}`;
      },
    },
    {
      field: 'top_pieces',
      headerName: 'Total Top Cut',
      width: 120,
      renderCell: (params: any) => {
        // Check if params and params.row exist
        if (!params || !params.row) return 0;
        
        const row = params.row as any; // Type assertion to access properties safely
        if (!row.design_no || !row.batch_number) return 0;
        
        // Find TOP design with same design number and batch number
        const designNo = row.design_no;
        const batchNumber = row.batch_number;
        const topDesign = designs.find(d => 
          d.design_no === designNo && 
          d.garment_part === 'TOP' && 
          d.batch_number === batchNumber
        );
        
        if (!topDesign) return 0;
        
        // Calculate total pieces
        return topDesign.total_pieces || 0;
      },
    },
    {
      field: 'bottom_pieces',
      headerName: 'Total Bottom Cut',
      width: 140,
      renderCell: (params: any) => {
        if (!params || !params.row) return 0;
        
        const row = params.row as any;
        if (!row.design_no || !row.batch_number) return 0;
        
        // Find BOTTOM design with same design number and batch number
        const designNo = row.design_no;
        const batchNumber = row.batch_number;
        const bottomDesign = designs.find(d => 
          d.design_no === designNo && 
          d.garment_part === 'BOTTOM' && 
          d.batch_number === batchNumber
        );
        
        if (!bottomDesign) return 0;
        
        // Calculate total pieces
        return bottomDesign.total_pieces || 0;
      },
    },
    {
      field: 'avg_fabric_per_piece_top',
      headerName: 'Avg/Pc Top',
      width: 120,
      renderCell: (params: any) => {
        if (!params || !params.row) return '-';
        
        const designNo = params.row.design_no;
        const batchNumber = params.row.batch_number;
        if (!designNo || !batchNumber) return '-';
        
        // Find TOP design with same design number and batch number
        const topDesign = designs.find(d => 
          d.design_no === designNo && 
          d.garment_part === 'TOP' && 
          d.batch_number === batchNumber
        );
        
        if (!topDesign) return '-';
        
        const total = [
          topDesign.xs_pieces,
          topDesign.s_pieces,
          topDesign.m_pieces,
          topDesign.l_pieces,
          topDesign.xl_pieces,
          topDesign.xxl_pieces,
          topDesign.three_xl_pieces
        ].reduce((sum, pieces) => sum + pieces, 0);
        
        return total > 0 ? (topDesign.fabric_meter / total).toFixed(2) : '-';
      }
    },
    {
      field: 'avg_fabric_per_piece_bottom',
      headerName: 'Avg/Pc Bottom',
      width: 130,
      renderCell: (params: any) => {
        if (!params || !params.row) return '-';
        
        const designNo = params.row.design_no;
        const batchNumber = params.row.batch_number;
        if (!designNo || !batchNumber) return '-';
        
        // Find BOTTOM design with same design number and batch number
        const bottomDesign = designs.find(d => 
          d.design_no === designNo && 
          d.garment_part === 'BOTTOM' && 
          d.batch_number === batchNumber
        );
        
        if (!bottomDesign) return '-';
        
        const total = [
          bottomDesign.xs_pieces,
          bottomDesign.s_pieces,
          bottomDesign.m_pieces,
          bottomDesign.l_pieces,
          bottomDesign.xl_pieces,
          bottomDesign.xxl_pieces,
          bottomDesign.three_xl_pieces
        ].reduce((sum, pieces) => sum + pieces, 0);
        
        return total > 0 ? (bottomDesign.fabric_meter / total).toFixed(2) : '-';
      }
    },
    {
      field: 'top_status',
      headerName: 'Top Status',
      width: 120,
      renderCell: (params: any) => {
        if (!params || !params.row) return '-';
        
        const row = params.row as any;
        if (!row.design_no || !row.batch_number) return '-';
        
        // Find TOP design with same design number and batch number
        const designNo = row.design_no;
        const batchNumber = row.batch_number;
        const topDesign = designs.find(d => 
          d.design_no === designNo && 
          d.garment_part === 'TOP' && 
          d.batch_number === batchNumber
        );
        
        if (!topDesign) return '-';
        
        return topDesign.status || '-';
      },
    },
    {
      field: 'bottom_status',
      headerName: 'Bottom Status',
      width: 140,
      renderCell: (params: any) => {
        if (!params || !params.row) return '-';
        
        const row = params.row as any;
        if (!row.design_no || !row.batch_number) return '-';
        
        // Find BOTTOM design with same design number and batch number
        const designNo = row.design_no;
        const batchNumber = row.batch_number;
        const bottomDesign = designs.find(d => 
          d.design_no === designNo && 
          d.garment_part === 'BOTTOM' && 
          d.batch_number === batchNumber
        );
        
        if (!bottomDesign) return '-';
        
        return bottomDesign.status || '-';
      },
    },
    {
      field: 'top_cutting_date',
      headerName: 'Top Cutting Date',
      width: 140,
      renderCell: (params: any) => {
        if (!params || !params.row) return '-';
        
        const row = params.row as any;
        if (!row.design_no || !row.batch_number) return '-';
        
        // Find TOP design with same design number and batch number
        const designNo = row.design_no;
        const batchNumber = row.batch_number;
        const topDesign = designs.find(d => 
          d.design_no === designNo && 
          d.garment_part === 'TOP' && 
          d.batch_number === batchNumber
        );
        
        if (!topDesign?.cutting_date) return '-';
        
        try {
          const date = new Date(topDesign.cutting_date);
          if (isNaN(date.getTime())) return '-';
          return date.toLocaleDateString();
        } catch (error) {
          return '-';
        }
      }
    },
    {
      field: 'bottom_cutting_date',
      headerName: 'Bottom Cutting Date',
      width: 160,
      renderCell: (params: any) => {
        if (!params || !params.row) return '-';
        
        const row = params.row as any;
        if (!row.design_no || !row.batch_number) return '-';
        
        // Find BOTTOM design with same design number and batch number
        const designNo = row.design_no;
        const batchNumber = row.batch_number;
        const bottomDesign = designs.find(d => 
          d.design_no === designNo && 
          d.garment_part === 'BOTTOM' && 
          d.batch_number === batchNumber
        );
        
        if (!bottomDesign?.cutting_date) return '-';
        
        try {
          const date = new Date(bottomDesign.cutting_date);
          if (isNaN(date.getTime())) return '-';
          return date.toLocaleDateString();
        } catch (error) {
          return '-';
        }
      }
    },
    {
      field: 'received_date',
      headerName: 'Received Date',
      width: 140,
      renderCell: (params: any) => {
        if (!params || !params.row) return '-';
        
        const row = params.row as any;
        if (!row.design_no || !row.batch_number) return '-';
        
        // Try to find the design with specific batch
        const designNo = row.design_no;
        const batchNumber = row.batch_number;
        const topDesign = designs.find(d => 
          d.design_no === designNo && 
          d.garment_part === 'TOP' && 
          d.batch_number === batchNumber
        );
        
        // Get received date from TOP design or directly from row
        const receivedDate = topDesign?.received_date || row?.received_date;
        if (!receivedDate) return '-';
        
        try {
          const date = new Date(receivedDate);
          if (isNaN(date.getTime())) return '-';
          return date.toLocaleDateString();
        } catch (error) {
          return '-';
        }
      }
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 160,
      hideable: false,
      renderCell: (params: GridRenderCellParams) => {
        if (!params || !params.row) return null;
        
        return (
          <Stack direction="row" spacing={1} justifyContent="flex-end">
            {canAdd && (
              <Tooltip title="Create New Batch">
                <IconButton
                  color="success"
                  onClick={() => handleCreateNewBatch(params.row.design_no)}
                  size="small"
                  sx={{ 
                    '&:hover': {
                      backgroundColor: 'success.light',
                      color: 'success.contrastText'
                    }
                  }}
                >
                  <ContentCopyIcon />
                </IconButton>
              </Tooltip>
            )}
            {canEdit && (
              <Tooltip title="Edit">
                <IconButton
                  color="primary"
                  onClick={() => handleEdit(params.row)}
                  size="small"
                  sx={{ 
                    '&:hover': {
                      backgroundColor: 'primary.light',
                      color: 'primary.contrastText'
                    }
                  }}
                >
                  <EditIcon />
                </IconButton>
              </Tooltip>
            )}
            {canDelete && !recordsWithAssignments.has(params.row.id) && (
              <Tooltip title="Delete">
                <IconButton
                  color="error"
                  onClick={() => handleDelete(params.row.id)}
                  size="small"
                  sx={{ 
                    '&:hover': {
                      backgroundColor: 'error.light',
                      color: 'error.contrastText'
                    }
                  }}
                >
                  <DeleteIcon />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        );
      },
    }
  ];

  // Add a function to prepare the data for the grid (no deduplication by design_no, each batch gets its own row)
  const prepareGridData = (designs: DesignCuttingType[]) => {
    // Create a map of design numbers + batch numbers to their first occurrence
    const designMap = new Map<string, DesignCuttingType>();
    
    // Each unique combination of design_no, batch_number gets its own row
    designs.forEach(design => {
      const key = `${design.design_no}_${design.batch_number}`;
      if (!designMap.has(key) || design.garment_part === 'TOP') {
        designMap.set(key, design);
      }
    });
    
    // Convert map to array and sort by design_no, then batch_number
    return Array.from(designMap.values()).sort((a, b) => {
      if (a.design_no !== b.design_no) {
        return a.design_no.localeCompare(b.design_no);
      }
      return a.batch_number - b.batch_number;
    });
  };

  // Add or update this function to apply filtering and deduplication
  const updateFilteredDesigns = () => {
    let filtered = designs;
    
    // Apply search query filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(design => 
        design.design_no.toLowerCase().includes(query) ||
        design.garment_part.toLowerCase().includes(query) ||
        design.status?.toLowerCase().includes(query)
      );
    }
    
    // Apply status filter - Inverted logic for showing pending designs
    if (showPendingOnly) {
      filtered = filtered.filter(design => {
        // Consider a design as pending if it has no cutting date
        return !design.cutting_date;
      });
    }
    

    
    // Deduplicate by design number
    const deduplicated = prepareGridData(filtered);
    setFilteredDesigns(deduplicated);
  };

  // Update the useEffect that handles filtering to use the new function
  useEffect(() => {
    console.log('Applying filters and deduplication');
    updateFilteredDesigns();
  }, [searchQuery, designs, showPendingOnly]);

  // Update the fetchDesigns function to call updateFilteredDesigns
  const fetchDesigns = async () => {
    console.log('DesignCutting: Starting to fetch designs');
    setLoading(true);
    setApiError(null);
    
    try {
      console.log('DesignCutting: Making API call to fetch designs');
      const [data, assignmentsRes] = await Promise.all([
        debugApiCall(
          () => designCuttingApi.getAll(),
          'Fetching design cutting records'
        ),
        api.get('/worker-assignments').catch(() => ({ data: [] }))
      ]);
      
      console.log('DesignCutting: API call successful, data received:', data);
      
      // Ensure we have a valid array
      const safeData = Array.isArray(data) ? data : [];
      console.log('DesignCutting: Safe data created, length:', safeData.length);
      
      // Check which design cutting records have worker assignments
      const assignments = assignmentsRes.data || [];
      const recordsWithAssignmentsSet = new Set<number>();
      
      safeData.forEach((design: any) => {
        const hasAssignments = assignments.some((wa: any) =>
          wa.design_no === design.design_no &&
          wa.garment_part === design.garment_part &&
          wa.batch_number === design.batch_number
        );
        if (hasAssignments) {
          recordsWithAssignmentsSet.add(design.id);
        }
      });
      
      setRecordsWithAssignments(recordsWithAssignmentsSet);
      console.log('DesignCutting: Records with assignments:', Array.from(recordsWithAssignmentsSet));
      
      // Debug full data shape issues
      if (safeData.length > 0) {
        const firstItem = safeData[0];
        console.log('DesignCutting: First item keys:', Object.keys(firstItem));
        console.log('DesignCutting: First item data types:', 
          Object.entries(firstItem).map(([key, value]) => 
            `${key}: ${typeof value}${value === null ? ' (null)' : ''}`
          )
        );
        
        // Check if all required columns have corresponding data
        const columnFields = columns.map(col => col.field);
        const missingColumns = columnFields.filter(field => !(field in firstItem));
        if (missingColumns.length > 0) {
          console.error('DesignCutting: Missing data for columns:', missingColumns);
        } else {
          console.log('DesignCutting: All columns have corresponding data fields');
        }
      }
      
      setDesigns(safeData);
      // Filtering and deduplication will be handled by the useEffect
      
      console.log('DesignCutting: State updated with designs data');
    } catch (error: any) {
      console.error('DesignCutting: Error fetching designs:', error);
      console.error('DesignCutting: Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      setError(error.response?.data?.error || 'Failed to fetch designs');
      // Initialize with empty arrays on error
      setDesigns([]);
      setFilteredDesigns([]);
    } finally {
      setLoading(false);
      console.log('DesignCutting: Loading state set to false');
    }
  };

  const fetchPendingToReceive = async () => {
    try {
      const data = await productionApi.getPendingToReceive('ISSUED');
      // Design Cutting should show both ISSUED and IN_PROGRESS internal stitching jobs
      // (some may have been moved to processing by mistake but still need cutting receive)
      const list = (data || []).filter((job: any) => {
        const s = String(job.status || '').toUpperCase();
        return s === 'ISSUED' || s === 'IN_PROGRESS' || s === 'PROCESSING';
      });
      setPendingToReceive(list);
    } catch (error) {
      console.error('Error fetching pending to receive:', error);
      setPendingToReceive([]);
    }
  };

  const fetchCompletedDesigns = async () => {
    try {
      const [inProcessRes, completeRes] = await Promise.all([
        api.get('/finishing/stitching/in-process'),
        api.get('/finishing/stitching/complete')
      ]);
      const inProcess = Array.isArray(inProcessRes.data) ? inProcessRes.data : [];
      const complete = Array.isArray(completeRes.data) ? completeRes.data : [];
      setCompletedDesigns(inProcess);
      setDispatchedDesigns(complete);
    } catch (error) {
      console.error('Error fetching completed designs:', error);
      setCompletedDesigns([]);
      setDispatchedDesigns([]);
    }
  };

  const handleReceiveAtStitching = async () => {
    if (!selectedJobWork) return;
    
    try {
      setLoading(true);
      await productionApi.receiveAtStitching({
        job_work_id: selectedJobWork.id,
        challan_date: challanDate
      });
      
      setSuccessMessage('Job received at stitching unit successfully!');
      setReceiveDialogOpen(false);
      setSelectedJobWork(null);
      
      // Refresh data
      fetchPendingToReceive();
      fetchDesigns();
      fetchCompletedDesigns();
    } catch (error: any) {
      setError(error.response?.data?.error || 'Failed to receive job at stitching unit');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    console.log('DesignCutting: Component mounted - Starting initialization');
    fetchDesigns();
    fetchPendingToReceive();
    fetchCompletedDesigns();
    
    return () => {
      console.log('DesignCutting: Component unmounted');
    };
  }, []);

  // Add a separate useEffect to monitor state changes
  useEffect(() => {
    console.log('DesignCutting: State update detected:', {
      loading,
      designsCount: designs.length,
      filteredDesignsCount: filteredDesigns.length,
      error,
      apiError
    });
  }, [loading, designs, filteredDesigns, error, apiError]);

  // Prefill TOP & BOTTOM size-wise pieces from Job Work when garmentPart is FULL_SET
  const [prefillDesignNo, setPrefillDesignNo] = useState<string | null>(null);

  const normalizeSize = (raw: string): string => {
    const s = String(raw || '').toUpperCase().trim();
    if (s === 'XXXL' || s === '3X' || s === '3XL ') return '3XL';
    if (s === '2XL') return 'XXL';
    return s;
  };

  const prefillFromJobWorkIfFullSet = async (designNo: string) => {
    try {
      const res = await api.get(`/job-works/by-design/${designNo}`);
      const jwList = Array.isArray(res.data) ? res.data : [];
      if (!jwList.length) return;

      // Prefer most recent job work that has FULL_SET in designDetails
      const withFullSet = jwList.filter((jw: any) => Array.isArray(jw.designDetails) && jw.designDetails.some((dd: any) => (dd.garmentPart || '').toUpperCase() === 'FULL_SET'));
      if (!withFullSet.length) return;

      // Pick the most recent by jobWorkDate if available, else the last
      const sorted = withFullSet.sort((a: any, b: any) => {
        const da = a.jobWorkDate ? new Date(a.jobWorkDate).getTime() : 0;
        const db = b.jobWorkDate ? new Date(b.jobWorkDate).getTime() : 0;
        return db - da;
      });
      const chosen = sorted[0];

      // Build size map from FULL_SET details
      const sizeToQty: Record<string, number> = {};
      (chosen.designDetails || []).forEach((dd: any) => {
        if ((dd.garmentPart || '').toUpperCase() !== 'FULL_SET') return;
        const size = normalizeSize(dd.size);
        const qty = Number(dd.pieces) || 0;
        if (!size) return;
        sizeToQty[size] = (sizeToQty[size] || 0) + qty;
      });

      if (Object.keys(sizeToQty).length === 0) return;

      // Map sizes to design cutting fields
      const toPiecesFields = (base: any) => ({
        ...base,
        xs_pieces: sizeToQty['XS'] || 0,
        s_pieces: sizeToQty['S'] || 0,
        m_pieces: sizeToQty['M'] || 0,
        l_pieces: sizeToQty['L'] || 0,
        xl_pieces: sizeToQty['XL'] || 0,
        xxl_pieces: sizeToQty['XXL'] || 0,
        three_xl_pieces: sizeToQty['3XL'] || 0,
      });

      // Ensure both sections are enabled
      setShowTopSection(true);
      setShowBottomSection(true);

      // Prefill TOP
      setTopFormData(prev => toPiecesFields(prev ? { ...prev } : {
        ...initialFormData,
        design_no: designNo,
        garment_part: 'TOP',
        received_date: formData.received_date || new Date().toISOString().split('T')[0],
        challan_date: formData.challan_date || new Date().toISOString().split('T')[0],
      }));

      // Prefill BOTTOM
      setBottomFormData(prev => toPiecesFields(prev ? { ...prev } : {
        ...initialFormData,
        design_no: designNo,
        garment_part: 'BOTTOM',
        received_date: formData.received_date || new Date().toISOString().split('T')[0],
        challan_date: formData.challan_date || new Date().toISOString().split('T')[0],
      }));

      setPrefillDesignNo(designNo);
    } catch (err) {
      console.error('Prefill from Job Work (FULL_SET) failed:', err);
    }
  };

  // Trigger prefill when design_no changes for create flow (avoid overriding during edit)
  useEffect(() => {
    if (editMode) return;
    const dn = formData.design_no;
    if (!dn) return;
    if (prefillDesignNo === dn) return; // already prefilling done for this design
    prefillFromJobWorkIfFullSet(dn);
  }, [formData.design_no]);

  const handleOpen = () => {
    setEditMode(false);
    setSelectedDesign(null);
    setSelectedPO(null);
    setImagePreview(null);
    setFormData({
      ...initialFormData,
      design_no: '',
      received_date: new Date().toISOString().split('T')[0],
      challan_date: new Date().toISOString().split('T')[0],
    });
    setTopFormData({
      ...initialFormData,
      garment_part: 'TOP',
      received_date: new Date().toISOString().split('T')[0],
      challan_date: new Date().toISOString().split('T')[0],
    });
    setBottomFormData(null);
    setJacketFormData(null);
    setShowTopSection(true);
    setShowBottomSection(false);
    setShowJacketSection(false);
    setSelectedTopId(undefined);
    setSelectedBottomId(undefined);
    setSelectedJacketId(undefined);
    setOpen(true);
  };

  const handleClose = () => {
    setImagePreview(null);
    setFormData(initialFormData);
    setTopFormData(null);
    setBottomFormData(null);
    setJacketFormData(null);
    setShowTopSection(true);
    setShowBottomSection(false);
    setShowJacketSection(false);
    setError('');
    setEditMode(false);
    setSelectedTopId(undefined);
    setSelectedBottomId(undefined);
    setSelectedJacketId(undefined);
    setFormReadOnly(false);
    setSelectedPO(null);
    setOpen(false);
  };

  const handleCreateNewBatch = (designNo: string) => {
    // Find the design to pre-fill the form with existing design details
    const existingDesign = designs.find(d => d.design_no === designNo);
    
    if (existingDesign) {
      // Set the selected design to pre-fill form data
      setSelectedDesign({
        design_no: designNo,
        image_url: existingDesign.image_url || ''
      } as any);
      
      // Set form data with current date
      setFormData({
        ...formData,
        design_no: designNo,
        received_date: new Date().toISOString().split('T')[0], // Today's date
        challan_date: new Date().toISOString().split('T')[0], // Today's date
      });
      
      // Show image preview
      setImagePreview(existingDesign.image_url || null);
    }
    
    // Reset other form states
    setEditMode(false);
    setSelectedTopId(undefined);
    setSelectedBottomId(undefined);
    setSelectedJacketId(undefined);
    setTopFormData(null);
    setBottomFormData(null);
    setJacketFormData(null);
    setShowTopSection(true);
    setShowBottomSection(false);
    setShowJacketSection(false);
    setError('');
    
    // Open the form dialog
    setOpen(true);
  };

  const handleEdit = (design: DesignCuttingType) => {
    setEditMode(true);
    
    // Get the design number to search for matching TOP, BOTTOM, and JACKET parts
    const designNo = design.design_no;
    
    // Find TOP, BOTTOM, and JACKET designs with the same design_no
    const topDesign = designs.find(d => d.design_no === designNo && d.garment_part === 'TOP');
    const bottomDesign = designs.find(d => d.design_no === designNo && d.garment_part === 'BOTTOM');
    const jacketDesign = designs.find(d => d.design_no === designNo && d.garment_part === 'JACKET');
    
    // Set image preview if exists
    if (design.image_url) {
      setImagePreview(design.image_url);
    } else {
      setImagePreview(null);
    }

    
    // Prepare the form data for general fields
    setFormData({
      design_no: designNo,
      received_date: design.received_date.split('T')[0],
      image_url: design.image_url,
      garment_part: design.garment_part,
      rate: design.rate || undefined,
      notes: design.notes || undefined,
      challan_date: design.challan_date ? design.challan_date.split('T')[0] : new Date().toISOString().split('T')[0],
      // Set other fields to default values since they'll be controlled by TOP/BOTTOM forms
      fabric_meter: 0,
      inner_fabric_meter: undefined,
      inner_received_date: undefined,
      xs_pieces: 0,
      s_pieces: 0,
      m_pieces: 0,
      l_pieces: 0,
      xl_pieces: 0,
      xxl_pieces: 0,
      three_xl_pieces: 0,
      cutting_date: undefined,
    });
    
    // Initialize TOP form data if it exists
    if (topDesign) {
      setTopFormData({
        design_no: topDesign.design_no,
        received_date: topDesign.received_date.split('T')[0],
        inner_received_date: topDesign.inner_received_date ? topDesign.inner_received_date.split('T')[0] : undefined,
        image_url: topDesign.image_url,
        garment_part: 'TOP',
        fabric_meter: topDesign.fabric_meter,
        inner_fabric_meter: topDesign.inner_fabric_meter || undefined,
        xs_pieces: topDesign.xs_pieces,
        s_pieces: topDesign.s_pieces,
        m_pieces: topDesign.m_pieces,
        l_pieces: topDesign.l_pieces,
        xl_pieces: topDesign.xl_pieces,
        xxl_pieces: topDesign.xxl_pieces,
        three_xl_pieces: topDesign.three_xl_pieces,
        cutting_date: topDesign.cutting_date ? topDesign.cutting_date.split('T')[0] : undefined,
        rate: topDesign.rate || undefined,
        notes: topDesign.notes || undefined,
        challan_date: topDesign.challan_date ? topDesign.challan_date.split('T')[0] : new Date().toISOString().split('T')[0],
      });
      setSelectedTopId(topDesign.id);
      setCurrentBatchNumber(topDesign.batch_number);
      setShowTopSection(true);
    } else {
      // Initialize with default values if no TOP design exists
      setTopFormData({
        ...initialFormData,
        design_no: designNo,
        garment_part: 'TOP',
        received_date: new Date().toISOString().split('T')[0],
        challan_date: new Date().toISOString().split('T')[0],
      });
      setSelectedTopId(undefined);
      setShowTopSection(false);
    }
    
    // Initialize BOTTOM form data if it exists
    if (bottomDesign) {
      setBottomFormData({
        design_no: bottomDesign.design_no,
        received_date: bottomDesign.received_date.split('T')[0],
        inner_received_date: bottomDesign.inner_received_date ? bottomDesign.inner_received_date.split('T')[0] : undefined,
        image_url: bottomDesign.image_url,
        garment_part: 'BOTTOM',
        fabric_meter: bottomDesign.fabric_meter,
        inner_fabric_meter: bottomDesign.inner_fabric_meter || undefined,
        xs_pieces: bottomDesign.xs_pieces,
        s_pieces: bottomDesign.s_pieces,
        m_pieces: bottomDesign.m_pieces,
        l_pieces: bottomDesign.l_pieces,
        xl_pieces: bottomDesign.xl_pieces,
        xxl_pieces: bottomDesign.xxl_pieces,
        three_xl_pieces: bottomDesign.three_xl_pieces,
        cutting_date: bottomDesign.cutting_date ? bottomDesign.cutting_date.split('T')[0] : undefined,
        rate: bottomDesign.rate || undefined,
        notes: bottomDesign.notes || undefined,
        challan_date: bottomDesign.challan_date ? bottomDesign.challan_date.split('T')[0] : new Date().toISOString().split('T')[0],
      });
      setSelectedBottomId(bottomDesign.id);
      setCurrentBatchNumber(bottomDesign.batch_number);
      setShowBottomSection(true);
    } else {
      setBottomFormData(null);
      setSelectedBottomId(undefined);
      setShowBottomSection(false);
    }
    
    // Initialize JACKET form data if it exists
    if (jacketDesign) {
      setJacketFormData({
        design_no: jacketDesign.design_no,
        received_date: jacketDesign.received_date.split('T')[0],
        inner_received_date: jacketDesign.inner_received_date ? jacketDesign.inner_received_date.split('T')[0] : undefined,
        image_url: jacketDesign.image_url,
        garment_part: 'JACKET',
        fabric_meter: jacketDesign.fabric_meter,
        inner_fabric_meter: jacketDesign.inner_fabric_meter || undefined,
        xs_pieces: jacketDesign.xs_pieces,
        s_pieces: jacketDesign.s_pieces,
        m_pieces: jacketDesign.m_pieces,
        l_pieces: jacketDesign.l_pieces,
        xl_pieces: jacketDesign.xl_pieces,
        xxl_pieces: jacketDesign.xxl_pieces,
        three_xl_pieces: jacketDesign.three_xl_pieces,
        cutting_date: jacketDesign.cutting_date ? jacketDesign.cutting_date.split('T')[0] : undefined,
        rate: jacketDesign.rate || undefined,
        notes: jacketDesign.notes || undefined,
        challan_date: jacketDesign.challan_date ? jacketDesign.challan_date.split('T')[0] : new Date().toISOString().split('T')[0],
      });
      setSelectedJacketId(jacketDesign.id);
      setCurrentBatchNumber(jacketDesign.batch_number);
      setShowJacketSection(true);
    } else {
      setJacketFormData(null);
      setSelectedJacketId(undefined);
      setShowJacketSection(false);
    }
    
    // Set the ID of the current design being edited
    setOpen(true);
  };

  // Update the input handlers for common fields
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    const numericFields = ['rate'];
    
    setFormData(prev => ({
      ...prev,
      [name]: numericFields.includes(name) ? Number(value) : value,
    }));
    
    // If design_no is changed, update it in both TOP and BOTTOM forms
    if (name === 'design_no') {
      if (topFormData) {
        setTopFormData({
          ...topFormData,
          design_no: value,
        });
      }
      if (bottomFormData) {
        setBottomFormData({
          ...bottomFormData,
          design_no: value,
        });
      }
    }
  };

  // Input handler for TOP section
  const handleTopInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    const numericFields = ['fabric_meter', 'inner_fabric_meter', 'xs_pieces', 's_pieces', 'm_pieces', 'l_pieces', 'xl_pieces', 'xxl_pieces', 'three_xl_pieces'];
    
    if (topFormData) {
      setTopFormData({
        ...topFormData,
        [name]: numericFields.includes(name) ? Number(value) : value,
      });
    }
  };

  // Input handler for BOTTOM section
  const handleBottomInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    const numericFields = ['fabric_meter', 'inner_fabric_meter', 'xs_pieces', 's_pieces', 'm_pieces', 'l_pieces', 'xl_pieces', 'xxl_pieces', 'three_xl_pieces'];
    
    if (bottomFormData) {
      setBottomFormData({
        ...bottomFormData,
        [name]: numericFields.includes(name) ? Number(value) : value,
      });
    } else {
      // Initialize BOTTOM form if it doesn't exist yet
      setBottomFormData({
        ...initialFormData,
        design_no: formData.design_no,
        garment_part: 'BOTTOM',
        received_date: new Date().toISOString().split('T')[0],
        challan_date: formData.challan_date,
        [name]: numericFields.includes(name) ? Number(value) : value,
      });
    }
  };

  // Toggle handler for TOP section
  const handleToggleTopSection = (event: React.ChangeEvent<HTMLInputElement>) => {
    const show = event.target.checked;
    setShowTopSection(show);
    
    // Initialize TOP form data if it doesn't exist
    if (show && !topFormData) {
      setTopFormData({
        ...initialFormData,
        design_no: formData.design_no,
        garment_part: 'TOP',
        received_date: new Date().toISOString().split('T')[0],
        challan_date: formData.challan_date,
      });
    }
    
    // If toggling off TOP section and all other sections are also off, toggle BOTTOM on
    if (!show && !showBottomSection && !showJacketSection) {
      setShowBottomSection(true);
      if (!bottomFormData) {
        setBottomFormData({
          ...initialFormData,
          design_no: formData.design_no,
          garment_part: 'BOTTOM',
          received_date: new Date().toISOString().split('T')[0],
          challan_date: formData.challan_date,
        });
      }
    }
  };

  // Toggle handler for BOTTOM section
  const handleToggleBottomSection = (event: React.ChangeEvent<HTMLInputElement>) => {
    const show = event.target.checked;
    setShowBottomSection(show);
    
    // Initialize BOTTOM form data if it doesn't exist
    if (show && !bottomFormData) {
      setBottomFormData({
        ...initialFormData,
        design_no: formData.design_no,
        garment_part: 'BOTTOM',
        received_date: new Date().toISOString().split('T')[0],
        challan_date: formData.challan_date,
      });
    }
    
    // If toggling off BOTTOM section and TOP section is also off, toggle TOP on
    if (!show && !showTopSection && !showJacketSection) {
      setShowTopSection(true);
      if (!topFormData) {
        setTopFormData({
          ...initialFormData,
          design_no: formData.design_no,
          garment_part: 'TOP',
          received_date: new Date().toISOString().split('T')[0],
          challan_date: formData.challan_date,
        });
      }
    }
  };

  // Input handler for JACKET section
  const handleJacketInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    const numericFields = ['fabric_meter', 'inner_fabric_meter', 'xs_pieces', 's_pieces', 'm_pieces', 'l_pieces', 'xl_pieces', 'xxl_pieces', 'three_xl_pieces'];
    
    if (jacketFormData) {
      setJacketFormData({
        ...jacketFormData,
        [name]: numericFields.includes(name) ? Number(value) : value,
      });
    } else {
      // Initialize JACKET form if it doesn't exist yet
      setJacketFormData({
        ...initialFormData,
        design_no: formData.design_no,
        garment_part: 'JACKET',
        received_date: new Date().toISOString().split('T')[0],
        challan_date: formData.challan_date,
        [name]: numericFields.includes(name) ? Number(value) : value,
      });
    }
  };

  // Toggle handler for JACKET section
  const handleToggleJacketSection = (event: React.ChangeEvent<HTMLInputElement>) => {
    const show = event.target.checked;
    setShowJacketSection(show);
    
    // Initialize JACKET form data if it doesn't exist
    if (show && !jacketFormData) {
      setJacketFormData({
        ...initialFormData,
        design_no: formData.design_no,
        garment_part: 'JACKET',
        received_date: new Date().toISOString().split('T')[0],
        challan_date: formData.challan_date,
      });
    }
    
    // If toggling off JACKET section and all other sections are also off, toggle TOP on
    if (!show && !showTopSection && !showBottomSection) {
      setShowTopSection(true);
      if (!topFormData) {
        setTopFormData({
          ...initialFormData,
          design_no: formData.design_no,
          garment_part: 'TOP',
          received_date: new Date().toISOString().split('T')[0],
          challan_date: formData.challan_date,
        });
      }
    }
  };

  // Helper: open the form pre-populated for a design number, with read-only flag
  const openFormForDesignNo = (designNo: string, readOnly: boolean) => {
    try {
      // Prefer TOP, else any record for this design
      const sample = designs.find(d => d.design_no === designNo && d.garment_part === 'TOP') || designs.find(d => d.design_no === designNo);
      if (sample) {
        setFormReadOnly(!!readOnly);
        handleEdit(sample as DesignCuttingType);
      } else {
        // If not present in current state, just initialize a minimal edit with design_no
        setFormReadOnly(!!readOnly);
        setEditMode(true);
        setFormData(prev => ({
          ...prev,
          design_no: designNo,
        }));
        setTopFormData(null);
        setBottomFormData(null);
        setShowTopSection(false);
        setShowBottomSection(false);
        setOpen(true);
      }
    } catch {
      setFormReadOnly(!!readOnly);
      setOpen(true);
    }
  };



  // Update the handleSubmit function with validation for at least one section
  const handleSubmit = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Form validation
      if (!selectedDesign && !formData.design_no) {
        setError('Please select a design or enter a design number');
        setLoading(false);
          return;
        }
      
      // Ensure at least one section is enabled
      if (!showTopSection && !showBottomSection && !showJacketSection) {
        setError('At least one section (TOP, BOTTOM, or JACKET) must be included');
        setLoading(false);
        return;
      }
      
      // Use selectedDesign.design_no if available, otherwise use formData.design_no
      const designNumber = selectedDesign ? selectedDesign.design_no : formData.design_no;
      
      // TOP validation
      if (showTopSection && topFormData) {
        if (!topFormData.received_date) {
          setError('TOP section: Received date is required');
          setLoading(false);
          return;
        }
        
        if (!topFormData.fabric_meter || topFormData.fabric_meter <= 0) {
          setError('TOP section: Fabric meter must be greater than zero');
          setLoading(false);
          return;
        }
      }
      
      // BOTTOM validation
      if (showBottomSection && bottomFormData) {
        if (!bottomFormData.received_date) {
          setError('BOTTOM section: Received date is required');
          setLoading(false);
          return;
        }
        
        if (!bottomFormData.fabric_meter || bottomFormData.fabric_meter <= 0) {
          setError('BOTTOM section: Fabric meter must be greater than zero');
          setLoading(false);
          return;
        }
      }
      
      // Use image URL from selected design
      let imageUrl = formData.image_url;
      
      // If a design was selected, use its image URL
      if (selectedDesign) {
        imageUrl = selectedDesign.image_url || undefined;
      }
      

      
      let anySuccess = false;
      let firstError: string | null = null;

      // JACKET validation
      if (showJacketSection && jacketFormData) {
        if (!jacketFormData.received_date) {
          setError('JACKET section: Received date is required');
          setLoading(false);
          return;
        }
        
        if (!jacketFormData.fabric_meter || jacketFormData.fabric_meter <= 0) {
          setError('JACKET section: Fabric meter must be greater than zero');
          setLoading(false);
          return;
        }
      }

      // Helper to find existing record id by composite when id not known
      const findExistingId = (part: 'TOP' | 'BOTTOM' | 'JACKET'): number | undefined => {
        const candidates = designs.filter(d => d.design_no === designNumber && d.garment_part === part);
        if (candidates.length === 0) return undefined;
        if (currentBatchNumber != null) {
          const inBatch = candidates.find(d => d.batch_number === currentBatchNumber);
          return inBatch ? inBatch.id : candidates[0].id;
        }
        return candidates[0].id;
      };

      // Include production_order_id if a PO was selected
      const poId = selectedPO?.id || undefined;

      // Process the TOP form if it exists (update if record exists, else create)
      if (showTopSection && topFormData) {
        const topData = {
          ...topFormData,
          design_no: designNumber,
          received_date: formData.received_date,
          challan_date: formData.challan_date,
          image_url: imageUrl,
          batch_number: currentBatchNumber,
          ...(poId ? { production_order_id: poId } : {}),
        } as any;

        try {
          const topId = selectedTopId || findExistingId('TOP');
          if (topId) {
            await designCuttingApi.update(topId, topData);
          } else {
            await designCuttingApi.create(topData);
          }
          anySuccess = true;
        } catch (e: any) {
          firstError = firstError || (e?.response?.data?.error || e?.message || 'Failed to save TOP');
        }
      }

      // Process the BOTTOM form if it exists (update if record exists, else create)
      if (showBottomSection && bottomFormData) {
        const bottomData = {
          ...bottomFormData,
          design_no: designNumber,
          received_date: formData.received_date,
          challan_date: formData.challan_date,
          image_url: imageUrl,
          batch_number: currentBatchNumber,
          ...(poId ? { production_order_id: poId } : {}),
        } as any;

        try {
          const bottomId = selectedBottomId || findExistingId('BOTTOM');
          if (bottomId) {
            await designCuttingApi.update(bottomId, bottomData);
          } else {
            await designCuttingApi.create(bottomData);
          }
          anySuccess = true;
        } catch (e: any) {
          firstError = firstError || (e?.response?.data?.error || e?.message || 'Failed to save BOTTOM');
        }
      }

      // Process the JACKET form if it exists (update if record exists, else create)
      if (showJacketSection && jacketFormData) {
        const jacketData = {
          ...jacketFormData,
          design_no: designNumber,
          received_date: formData.received_date,
          challan_date: formData.challan_date,
          image_url: imageUrl,
          batch_number: currentBatchNumber,
          ...(poId ? { production_order_id: poId } : {}),
        } as any;

        try {
          const jacketId = selectedJacketId || findExistingId('JACKET');
          if (jacketId) {
            await designCuttingApi.update(jacketId, jacketData);
          } else {
            await designCuttingApi.create(jacketData);
          }
          anySuccess = true;
        } catch (e: any) {
          firstError = firstError || (e?.response?.data?.error || e?.message || 'Failed to save JACKET');
        }
      }

      if (!anySuccess && firstError) {
        throw new Error(firstError);
      }
      
      // Reload designs after successful submission
      fetchDesigns();
      setSuccessMessage(editMode ? 'Design updated successfully' : 'Design created successfully');
      handleClose();
    } catch (error: any) {
      console.error('Error submitting design:', error);
      setError(error.message || 'Failed to save design');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: number) => {
    try {
      // Get the design details
      const design = designs.find(d => d.id === id);
      if (!design) {
        setError('Design not found');
        return;
      }

      // Check for worker assignments before allowing deletion
      try {
        const [workerAssignmentsRes, jobWorksRes] = await Promise.all([
          api.get('/worker-assignments'),
          api.get(`/job-works/by-design/${design.design_no}`).catch(() => ({ data: [] }))
        ]);
        
        const allAssignments = workerAssignmentsRes.data || [];
        const assignmentsForThisRecord = allAssignments.filter((wa: any) =>
          wa.design_no === design.design_no &&
          wa.garment_part === design.garment_part &&
          wa.batch_number === design.batch_number
        );

        if (assignmentsForThisRecord.length > 0) {
          setError(`Cannot delete this design cutting record. ${assignmentsForThisRecord.length} worker assignment(s) exist for this record. Please remove worker assignments first.`);
          return;
        }

        // Note: Job works in PROCESSING state will be automatically moved to ISSUED by the backend
      } catch (assignmentError: any) {
        console.error('Error checking worker assignments or job works:', assignmentError);
        // Continue with deletion attempt - backend will also check
      }

      // Check if both TOP and BOTTOM parts exist for this design number
      const designNo = design.design_no;
      const topDesign = designs.find(d => d.design_no === designNo && d.garment_part === 'TOP');
      const bottomDesign = designs.find(d => d.design_no === designNo && d.garment_part === 'BOTTOM');

      // If both parts exist, ask which one(s) to delete
      if (topDesign && bottomDesign) {
        const options = [
          `Delete only ${designNo} TOP`,
          `Delete only ${designNo} BOTTOM`,
          `Delete both ${designNo} TOP and BOTTOM`
        ];
        
        const userChoice = window.prompt(
          `${designNo} has both TOP and BOTTOM parts. What would you like to delete?\n\n` +
          `1. ${options[0]}\n` +
          `2. ${options[1]}\n` +
          `3. ${options[2]}\n\n` +
          `Please enter 1, 2, or 3:`
        );

        if (!userChoice) {
          // User cancelled
          return;
        }

        const choice = parseInt(userChoice);
        if (isNaN(choice) || choice < 1 || choice > 3) {
          setError('Invalid choice. Please try again.');
          return;
        }

        let totalJobWorksUpdated = 0;
        
        switch (choice) {
          case 1: // Delete TOP only
            if (window.confirm('Are you sure you want to delete this item?')) {
              const topResponse = await designCuttingApi.delete(topDesign.id);
              totalJobWorksUpdated += (topResponse?.jobWorksUpdated || 0);
            }
            break;
          case 2: // Delete BOTTOM only
            if (window.confirm('Are you sure you want to delete this item?')) {
              const bottomResponse = await designCuttingApi.delete(bottomDesign.id);
              totalJobWorksUpdated += (bottomResponse?.jobWorksUpdated || 0);
            }
            break;
          case 3: // Delete both
            if (window.confirm('Are you sure you want to delete this item?')) {
              const [topResponse, bottomResponse] = await Promise.all([
                designCuttingApi.delete(topDesign.id),
                designCuttingApi.delete(bottomDesign.id)
              ]);
              totalJobWorksUpdated += (topResponse?.jobWorksUpdated || 0);
              totalJobWorksUpdated += (bottomResponse?.jobWorksUpdated || 0);
            }
            break;
        }
        
        await fetchDesigns();
        
        if (totalJobWorksUpdated > 0) {
          setSuccessMessage(`Design cutting deleted successfully. ${totalJobWorksUpdated} job work(s) moved from PROCESSING to ISSUED state.`);
        } else {
          setSuccessMessage('Design cutting deleted successfully');
        }
      } else {
        // Only one part exists, confirm and delete
        const part = design.garment_part;
        if (window.confirm(`Are you sure you want to delete ${designNo} ${part}?`)) {
          const response = await designCuttingApi.delete(id);
          const jobWorksUpdated = response?.jobWorksUpdated || 0;
          
          await fetchDesigns();
          
          if (jobWorksUpdated > 0) {
            setSuccessMessage(`Design cutting deleted successfully. ${jobWorksUpdated} job work(s) moved from PROCESSING to ISSUED state.`);
          } else {
            setSuccessMessage('Design cutting deleted successfully');
          }
        }
      }
    } catch (error: any) {
      console.error('Error deleting design:', error);
      setError(error.response?.data?.error || 'Failed to delete design');
    }
  };

  // at the end of the component, before the return, add this debug log
  useEffect(() => {
    renderDebug.log(`Designs state updated, count: ${designs.length}`);
  }, [designs]);
  
  useEffect(() => {
    renderDebug.log(`Filtered designs updated, count: ${filteredDesigns.length}`);
  }, [filteredDesigns]);
  
  // before returning the JSX, add this debug log
  renderDebug.log(`Rendering with ${filteredDesigns.length} designs, loading: ${loading}`);
  renderDebug.end();

  const renderFormContent = () => (
    <Box component="form" sx={{ mt: 2 }}>
      <Grid container spacing={3}>
        {/* Common Section - Design Selection and Information */}
        <Grid item xs={12}>
          <Typography variant="h6" color="primary" gutterBottom>
            Design Information
          </Typography>
        </Grid>

        {/* Production Order Selector (optional — prefills design & sizes) */}
        {!editMode && (
          <Grid item xs={12}>
            <Autocomplete
              id="po-select"
              options={productionOrders}
              loading={loadingPOs}
              value={selectedPO}
              onChange={handlePOChange}
              getOptionLabel={(option) =>
                `${option.order_number} — ${option.design_no} (${option.status})`
              }
              renderOption={(props, option) => (
                <Box component="li" {...props} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  {option.design?.image_url ? (
                    <Box
                      component="img"
                      src={option.design.image_url}
                      alt={option.design_no}
                      sx={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 1 }}
                    />
                  ) : (
                    <Box sx={{ width: 40, height: 40, bgcolor: 'grey.200', borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <NoImageIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                    </Box>
                  )}
                  <Box>
                    <Typography variant="body2" fontWeight="medium">
                      {option.order_number} — {option.design_no}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {option.status} | Planned: {option.total_planned} pcs
                      {option.target_date
                        ? ` | Target: ${new Date(option.target_date).toLocaleDateString('en-IN')}`
                        : ''}
                    </Typography>
                  </Box>
                </Box>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Link to Production Order (optional)"
                  placeholder="Search by PO number or design number"
                  helperText="Select a Production Order to auto-fill design and size quantities"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loadingPOs ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              filterOptions={(options, state) => {
                const input = state.inputValue.toLowerCase();
                return options.filter(
                  (o) =>
                    o.order_number.toLowerCase().includes(input) ||
                    o.design_no.toLowerCase().includes(input)
                );
              }}
            />
          </Grid>
        )}

        {/* Design Selection Dropdown */}
        <Grid item xs={12}>
          <Autocomplete
            id="design-select"
            options={designsList}
            loading={loadingDesigns}
            value={selectedDesign}
            onChange={handleDesignChange}
            getOptionLabel={(option) => `${option.design_no} - ${option.name || 'Unnamed Design'}`}
            renderOption={(props, option) => (
              <Box component="li" {...props} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                {option.image_url ? (
                  <Box
                    component="img"
                    src={option.image_url}
                    alt={option.design_no}
                    sx={{ width: 50, height: 50, objectFit: 'cover', borderRadius: 1 }}
                  />
                ) : (
                  <Box sx={{ width: 50, height: 50, bgcolor: 'grey.200', borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <NoImageIcon sx={{ color: 'text.secondary' }} />
                  </Box>
                )}
                <Box>
                  <Typography variant="body1" fontWeight="medium">{option.design_no}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {option.name || 'Unnamed'} • {option.product_type || 'No type'}
                  </Typography>
                </Box>
              </Box>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Select Design"
                placeholder="Search for design by number or name"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loadingDesigns ? <CircularProgress color="inherit" size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
        </Grid>

        {/* Design Preview and Details */}
        {selectedDesign && (
          <>
            <Grid item xs={12}>
              <Typography variant="subtitle1" gutterBottom>
                Selected Design Preview
              </Typography>
              <Box sx={{ 
        display: 'flex',
                flexDirection: 'row', 
                flexWrap: 'wrap',
                gap: 2, 
                justifyContent: 'center',
                mb: 3 
              }}>
                {/* Main Image */}
                {selectedDesign.image_url && (
                  <Box
                    component="img"
                    src={selectedDesign.image_url}
                    alt={`Design ${selectedDesign.design_no}`}
                    sx={{ 
                      width: 'auto',
                      height: 250,
                      maxWidth: '100%',
                      objectFit: 'contain',
                      borderRadius: 1,
                      border: '1px solid',
                      borderColor: 'divider'
                    }}
                  />
                )}
                
                {/* Additional Images */}
                {selectedDesign.image2_url && (
                  <Box
                    component="img"
                    src={selectedDesign.image2_url}
                    alt={`Design ${selectedDesign.design_no} - 2`}
        sx={{ 
                      width: 'auto',
                      height: 250,
                      maxWidth: '100%',
                      objectFit: 'contain',
                      borderRadius: 1,
                      border: '1px solid',
                      borderColor: 'divider'
                    }}
                  />
                )}
                {selectedDesign.image3_url && (
                  <Box
                    component="img"
                    src={selectedDesign.image3_url}
                    alt={`Design ${selectedDesign.design_no} - 3`}
                    sx={{ 
                      width: 'auto',
                      height: 250,
                      maxWidth: '100%',
                      objectFit: 'contain',
                      borderRadius: 1,
                      border: '1px solid',
                      borderColor: 'divider'
                    }}
                  />
                )}
                {selectedDesign.image4_url && (
                  <Box
                    component="img"
                    src={selectedDesign.image4_url}
                    alt={`Design ${selectedDesign.design_no} - 4`}
                    sx={{ 
                      width: 'auto',
                      height: 250,
                      maxWidth: '100%',
                      objectFit: 'contain',
                      borderRadius: 1,
                      border: '1px solid',
                      borderColor: 'divider'
                    }}
                  />
                )}
              </Box>
            </Grid>
            
            <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
                label="Design Number"
                value={selectedDesign.design_no || ''}
            InputProps={{
                  startAdornment: <InputAdornment position="start">#</InputAdornment>,
                  readOnly: true,
                }}
          />
        </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Product Type"
                value={selectedDesign.product_type || ''}
                InputProps={{
                  readOnly: true,
                }}
          />
        </Grid>
          </>
        )}


            
        {/* Maintain the design_no input field for backward compatibility, but hide it if a design is selected */}
        {!selectedDesign && (
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Design Number"
                name="design_no"
                value={formData.design_no}
                onChange={handleInputChange}
                required
                disabled={formReadOnly}
              InputProps={{
                startAdornment: <InputAdornment position="start">#</InputAdornment>,
              }}
              placeholder="Enter unique design number"
              />
            </Grid>
        )}

        <Grid item xs={12} sm={selectedDesign ? 12 : 6}>
          <TextField
            fullWidth
            type="date"
            label="Challan Date"
            name="challan_date"
            value={formData.challan_date}
            onChange={handleInputChange}
            InputLabelProps={{ shrink: true }}
            disabled={formReadOnly}
          />
        </Grid>

        {/* TOP Section Toggle & Header */}
        <Grid item xs={12} sx={{ mt: 2 }}>
          <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h6" color="primary">
              TOP Section
            </Typography>
            <FormControlLabel
              control={
                <Switch
                  checked={showTopSection}
                  onChange={handleToggleTopSection}
                  color="primary"
                  disabled={formReadOnly || (topFormData !== null && editMode && selectedTopId !== undefined)}
                />
              }
              label={topFormData !== null && editMode && selectedTopId !== undefined ? "TOP data exists" : "Include TOP"}
            />
          </Box>
        </Grid>
        
        {/* TOP Section Content - Only shown when enabled */}
        {showTopSection && topFormData && (
          <>
            {/* TOP Section - Main Data */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="date"
                label="Received Date"
                name="received_date"
                value={topFormData.received_date}
                onChange={handleTopInputChange}
                InputLabelProps={{ shrink: true }}
                required
                disabled={formReadOnly}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="number"
                label="Fabric Meter"
                name="fabric_meter"
                value={topFormData.fabric_meter}
                onChange={handleTopInputChange}
                InputProps={{
                  endAdornment: <InputAdornment position="end">m</InputAdornment>,
                }}
                required
                disabled={formReadOnly}
              />
            </Grid>

            {/* TOP Section - Inner Fabric Details */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" color="textSecondary">
                Inner Fabric Details
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="date"
                label="Inner Received Date"
                name="inner_received_date"
                value={topFormData.inner_received_date || ''}
                onChange={handleTopInputChange}
                InputLabelProps={{ shrink: true }}
                disabled={formReadOnly}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="number"
                label="Inner Fabric Meter"
                name="inner_fabric_meter"
                value={topFormData.inner_fabric_meter || ''}
                onChange={handleTopInputChange}
                InputProps={{
                  endAdornment: <InputAdornment position="end">m</InputAdornment>,
                }}
                disabled={formReadOnly}
              />
            </Grid>

            {/* TOP Section - Cutting Date */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="date"
                label="Cutting Date"
                name="cutting_date"
                value={topFormData.cutting_date || ''}
                onChange={handleTopInputChange}
                InputLabelProps={{ shrink: true }}
                disabled={formReadOnly}
              />
            </Grid>

            {/* TOP Section - Size Details */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" color="textSecondary" sx={{ mb: 2 }}>
                Size Details (TOP)
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6} sm={3}>
                  <TextField
                    fullWidth
                    type="number"
                    label="XS"
                    name="xs_pieces"
                    value={topFormData.xs_pieces}
                    onChange={handleTopInputChange}
                    disabled={formReadOnly}
                  />
            </Grid>
                <Grid item xs={6} sm={3}>
                  <TextField
                    fullWidth
                    type="number"
                    label="S"
                    name="s_pieces"
                    value={topFormData.s_pieces}
                    onChange={handleTopInputChange}
                    disabled={formReadOnly}
                  />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <TextField
                    fullWidth
                    type="number"
                    label="M"
                    name="m_pieces"
                    value={topFormData.m_pieces}
                    onChange={handleTopInputChange}
                    disabled={formReadOnly}
                  />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <TextField
                    fullWidth
                    type="number"
                    label="L"
                    name="l_pieces"
                    value={topFormData.l_pieces}
                    onChange={handleTopInputChange}
                    disabled={formReadOnly}
                  />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <TextField
                    fullWidth
                    type="number"
                    label="XL"
                    name="xl_pieces"
                    value={topFormData.xl_pieces}
                    onChange={handleTopInputChange}
                    disabled={formReadOnly}
                  />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <TextField
                    fullWidth
                    type="number"
                    label="XXL"
                    name="xxl_pieces"
                    value={topFormData.xxl_pieces}
                    onChange={handleTopInputChange}
                    disabled={formReadOnly}
                  />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <TextField
                    fullWidth
                    type="number"
                    label="3XL"
                    name="three_xl_pieces"
                    value={topFormData.three_xl_pieces}
                    onChange={handleTopInputChange}
                    disabled={formReadOnly}
                  />
                </Grid>
              </Grid>
            </Grid>
          </>
        )}

        {/* BOTTOM Section Toggle */}
        <Grid item xs={12} sx={{ mt: 2 }}>
          <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h6" color="primary">
              BOTTOM Section
            </Typography>
            <FormControlLabel
              control={
                <Switch
                  checked={showBottomSection}
                  onChange={handleToggleBottomSection}
                  color="primary"
                  disabled={formReadOnly || (bottomFormData !== null && editMode && selectedBottomId !== undefined)}
                />
              }
              label={bottomFormData !== null && editMode && selectedBottomId !== undefined ? "BOTTOM data exists" : "Include BOTTOM"}
            />
          </Box>
        </Grid>
        
        {/* BOTTOM Section Content - Only shown when enabled */}
        {showBottomSection && bottomFormData && (
          <>
            {/* BOTTOM Section - Main Data */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="date"
                label="Received Date"
                name="received_date"
                value={bottomFormData.received_date}
                onChange={handleBottomInputChange}
                InputLabelProps={{ shrink: true }}
                required
                disabled={formReadOnly}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="number"
                label="Fabric Meter"
                name="fabric_meter"
                value={bottomFormData.fabric_meter}
                onChange={handleBottomInputChange}
                InputProps={{
                  endAdornment: <InputAdornment position="end">m</InputAdornment>,
                }}
                required
                disabled={formReadOnly}
              />
            </Grid>

            {/* BOTTOM Section - Inner Fabric Details */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" color="textSecondary">
                Inner Fabric Details (BOTTOM)
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="date"
                label="Inner Received Date"
                name="inner_received_date"
                value={bottomFormData.inner_received_date || ''}
                onChange={handleBottomInputChange}
                InputLabelProps={{ shrink: true }}
                disabled={formReadOnly}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="number"
                label="Inner Fabric Meter"
                name="inner_fabric_meter"
                value={bottomFormData.inner_fabric_meter || ''}
                onChange={handleBottomInputChange}
                InputProps={{
                  endAdornment: <InputAdornment position="end">m</InputAdornment>,
                }}
                disabled={formReadOnly}
              />
            </Grid>

            {/* BOTTOM Section - Cutting Date */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="date"
                label="Cutting Date"
                name="cutting_date"
                value={bottomFormData.cutting_date || ''}
                onChange={handleBottomInputChange}
                InputLabelProps={{ shrink: true }}
                disabled={formReadOnly}
              />
            </Grid>

            {/* BOTTOM Section - Size Details */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" color="textSecondary" sx={{ mb: 2 }}>
                Size Details (BOTTOM)
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6} sm={3}>
              <TextField
                fullWidth
                    type="number"
                label="XS"
                name="xs_pieces"
                    value={bottomFormData.xs_pieces}
                    onChange={handleBottomInputChange}
                disabled={formReadOnly}
              />
            </Grid>
                <Grid item xs={6} sm={3}>
              <TextField
                fullWidth
                    type="number"
                label="S"
                name="s_pieces"
                    value={bottomFormData.s_pieces}
                    onChange={handleBottomInputChange}
                disabled={formReadOnly}
              />
            </Grid>
                <Grid item xs={6} sm={3}>
              <TextField
                fullWidth
                    type="number"
                label="M"
                name="m_pieces"
                    value={bottomFormData.m_pieces}
                    onChange={handleBottomInputChange}
                disabled={formReadOnly}
              />
            </Grid>
                <Grid item xs={6} sm={3}>
              <TextField
                fullWidth
                    type="number"
                label="L"
                name="l_pieces"
                    value={bottomFormData.l_pieces}
                    onChange={handleBottomInputChange}
                disabled={formReadOnly}
              />
            </Grid>
                <Grid item xs={6} sm={3}>
              <TextField
                fullWidth
                    type="number"
                label="XL"
                name="xl_pieces"
                    value={bottomFormData.xl_pieces}
                    onChange={handleBottomInputChange}
                disabled={formReadOnly}
              />
            </Grid>
                <Grid item xs={6} sm={3}>
              <TextField
                fullWidth
                    type="number"
                label="XXL"
                name="xxl_pieces"
                    value={bottomFormData.xxl_pieces}
                    onChange={handleBottomInputChange}
                  disabled={formReadOnly}
                  />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <TextField
                    fullWidth
                type="number"
                    label="3XL"
                    name="three_xl_pieces"
                    value={bottomFormData.three_xl_pieces}
                    onChange={handleBottomInputChange}
                disabled={formReadOnly}
              />
            </Grid>
              </Grid>
            </Grid>
          </>
        )}

        {/* JACKET Section Toggle */}
        <Grid item xs={12} sx={{ mt: 2 }}>
          <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h6" color="primary">
              JACKET Section
            </Typography>
            <FormControlLabel
              control={
                <Switch
                  checked={showJacketSection}
                  onChange={handleToggleJacketSection}
                  color="primary"
                  disabled={formReadOnly || (jacketFormData !== null && editMode && selectedJacketId !== undefined)}
                />
              }
              label={jacketFormData !== null && editMode && selectedJacketId !== undefined ? "JACKET data exists" : "Include JACKET"}
            />
          </Box>
        </Grid>
        
        {/* JACKET Section Content - Only shown when enabled */}
        {showJacketSection && jacketFormData && (
          <>
            {/* JACKET Section - Main Data */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="date"
                label="Received Date"
                name="received_date"
                value={jacketFormData.received_date}
                onChange={handleJacketInputChange}
                InputLabelProps={{ shrink: true }}
                required
                disabled={formReadOnly}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="number"
                label="Fabric Meter"
                name="fabric_meter"
                value={jacketFormData.fabric_meter}
                onChange={handleJacketInputChange}
                InputProps={{
                  endAdornment: <InputAdornment position="end">m</InputAdornment>,
                }}
                required
                disabled={formReadOnly}
              />
            </Grid>

            {/* JACKET Section - Inner Fabric Details */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" color="textSecondary">
                Inner Fabric Details (JACKET)
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="date"
                label="Inner Received Date"
                name="inner_received_date"
                value={jacketFormData.inner_received_date || ''}
                onChange={handleJacketInputChange}
                InputLabelProps={{ shrink: true }}
                disabled={formReadOnly}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="number"
                label="Inner Fabric Meter"
                name="inner_fabric_meter"
                value={jacketFormData.inner_fabric_meter || ''}
                onChange={handleJacketInputChange}
                InputProps={{
                  endAdornment: <InputAdornment position="end">m</InputAdornment>,
                }}
                disabled={formReadOnly}
              />
            </Grid>

            {/* JACKET Section - Cutting Date */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="date"
                label="Cutting Date"
                name="cutting_date"
                value={jacketFormData.cutting_date || ''}
                onChange={handleJacketInputChange}
                InputLabelProps={{ shrink: true }}
                disabled={formReadOnly}
              />
            </Grid>

            {/* JACKET Section - Size Details */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" color="textSecondary" sx={{ mb: 2 }}>
                Size Details (JACKET)
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6} sm={3}>
                  <TextField
                    fullWidth
                    type="number"
                    label="XS"
                    name="xs_pieces"
                    value={jacketFormData.xs_pieces}
                    onChange={handleJacketInputChange}
                    disabled={formReadOnly}
                  />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <TextField
                    fullWidth
                    type="number"
                    label="S"
                    name="s_pieces"
                    value={jacketFormData.s_pieces}
                    onChange={handleJacketInputChange}
                    disabled={formReadOnly}
                  />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <TextField
                    fullWidth
                    type="number"
                    label="M"
                    name="m_pieces"
                    value={jacketFormData.m_pieces}
                    onChange={handleJacketInputChange}
                    disabled={formReadOnly}
                  />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <TextField
                    fullWidth
                    type="number"
                    label="L"
                    name="l_pieces"
                    value={jacketFormData.l_pieces}
                    onChange={handleJacketInputChange}
                    disabled={formReadOnly}
                  />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <TextField
                    fullWidth
                    type="number"
                    label="XL"
                    name="xl_pieces"
                    value={jacketFormData.xl_pieces}
                    onChange={handleJacketInputChange}
                    disabled={formReadOnly}
                  />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <TextField
                    fullWidth
                    type="number"
                    label="XXL"
                    name="xxl_pieces"
                    value={jacketFormData.xxl_pieces}
                    onChange={handleJacketInputChange}
                    disabled={formReadOnly}
                  />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <TextField
                    fullWidth
                    type="number"
                    label="3XL"
                    name="three_xl_pieces"
                    value={jacketFormData.three_xl_pieces}
                    onChange={handleJacketInputChange}
                    disabled={formReadOnly}
                  />
                </Grid>
              </Grid>
            </Grid>
          </>
        )}

        {/* Common Bottom Section - Rate and Notes */}
            <Grid item xs={12}>
          <Typography variant="h6" color="primary" sx={{ mt: 3, mb: 1 }}>
                Additional Information
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
            type="number"
                label="Rate"
                name="rate"
                value={formData.rate || ''}
                onChange={handleInputChange}
                InputProps={{
              startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                }}
                disabled={formReadOnly}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
            label="Notes"
            name="notes"
                value={formData.notes || ''}
                onChange={handleInputChange}
                disabled={formReadOnly}
              />
            </Grid>
      </Grid>
    </Box>
  );

  // Update the function that opens the preview image
  const openImagePreview = async (url: string, designNo: string) => {
    setPreviewImage(url);
    
    // Find TOP and BOTTOM designs with the same design number
    const topDesign = designs.find(d => d.design_no === designNo && d.garment_part === 'TOP');
    const bottomDesign = designs.find(d => d.design_no === designNo && d.garment_part === 'BOTTOM');
    
    // Store selected design information for the preview dialog
    setSelectedPreviewDesignNo(designNo);
    setSelectedPreviewTopDesign(topDesign);
    setSelectedPreviewBottomDesign(bottomDesign);
    
    // Fetch main design data for job work records and other details
    try {
      const designsResponse = await designApi.getAll();
      const mainDesign = designsResponse.data.find((d: Design) => d.design_no === designNo);
      setSelectedDesign(mainDesign || null);
    } catch (error) {
      console.error('Error fetching main design data:', error);
      setSelectedDesign(null);
    }
    
    setPreviewDialogOpen(true);
  };

  // Add a function to close the preview dialog
  const closeImagePreview = () => {
    setPreviewDialogOpen(false);
    setPreviewImage(null);
    setSelectedPreviewDesignNo('');
    setSelectedPreviewTopDesign(undefined);
    setSelectedPreviewBottomDesign(undefined);
    setSelectedDesign(null);
  };



  // Add function to fetch designs
  const fetchDesignsList = async () => {
    try {
      setLoadingDesigns(true);
      // Load all designs via paginated summary API
      const pageSize = 200;
      let page = 1;
      let all: Design[] = [] as any;
      while (true) {
        const resp = await api.get('/designs', { params: { summary: 1, page, pageSize, withCount: 1 } });
        const data = Array.isArray(resp.data) ? resp.data : [];
        all = all.concat(data as any);
        const total = Number(resp.headers['x-total-count'] || 0);
        if (total > 0) {
          if (page * pageSize >= total) break;
        } else {
          if (data.length < pageSize) break;
        }
        page += 1;
      }
      console.log('Fetched designs for dropdown (all pages):', all.length);
      setDesignsList(all);
    } catch (error) {
      console.error('Error fetching designs:', error);
      // Show a notification instead of breaking the form
      setError('Could not load designs for dropdown. You can still enter design number manually.');
    } finally {
      setLoadingDesigns(false);
    }
  };
  
  // Fetch production orders for PO selector
  const fetchProductionOrders = async () => {
    try {
      setLoadingPOs(true);
      const data = await productionOrdersApi.getAll();
      // Only show POs that have planned_qty and are not CANCELLED/COMPLETED
      const activePOs = data.filter(
        (po) => po.planned_qty && po.status !== 'CANCELLED' && po.status !== 'COMPLETED'
      );
      setProductionOrders(activePOs);
    } catch (error) {
      console.error('Error fetching production orders:', error);
    } finally {
      setLoadingPOs(false);
    }
  };

  // Call fetchDesignsList and fetchProductionOrders on component mount
  useEffect(() => {
    fetchDesignsList();
    fetchProductionOrders();
  }, []);

  // Handler for Production Order selection — prefills design_no and sizes
  const handlePOChange = (_event: React.SyntheticEvent, po: ProductionOrder | null) => {
    setSelectedPO(po);

    if (!po) return;

    // Find matching design from designsList to get image preview
    const matchedDesign = designsList.find(
      (d) => d.design_no === po.design_no
    );

    if (matchedDesign) {
      setSelectedDesign(matchedDesign);
      setImagePreview(matchedDesign.image_url || null);
    }

    // Set design_no
    setFormData((prev) => ({
      ...prev,
      design_no: po.design_no,
      image_url: matchedDesign?.image_url || '',
    }));

    // Pre-fill sizes from PO's planned_qty
    const planned = po.planned_qty as Record<string, Record<string, number>> | null;
    if (!planned) return;

    const mapSizes = (sizeMap: Record<string, number> | undefined) => ({
      xs_pieces: sizeMap?.['XS'] || 0,
      s_pieces: sizeMap?.['S'] || 0,
      m_pieces: sizeMap?.['M'] || 0,
      l_pieces: sizeMap?.['L'] || 0,
      xl_pieces: sizeMap?.['XL'] || 0,
      xxl_pieces: sizeMap?.['XXL'] || 0,
      three_xl_pieces: sizeMap?.['3XL'] || 0,
    });

    const today = new Date().toISOString().split('T')[0];

    // TOP
    if (planned['TOP']) {
      setShowTopSection(true);
      setTopFormData({
        ...initialFormData,
        design_no: po.design_no,
        garment_part: 'TOP',
        received_date: today,
        challan_date: formData.challan_date || today,
        ...mapSizes(planned['TOP']),
      });
      setSelectedTopId(undefined);
    } else {
      setShowTopSection(false);
      setTopFormData(null);
    }

    // BOTTOM
    if (planned['BOTTOM']) {
      setShowBottomSection(true);
      setBottomFormData({
        ...initialFormData,
        design_no: po.design_no,
        garment_part: 'BOTTOM',
        received_date: today,
        challan_date: formData.challan_date || today,
        ...mapSizes(planned['BOTTOM']),
      });
      setSelectedBottomId(undefined);
    } else {
      setShowBottomSection(false);
      setBottomFormData(null);
    }

    // JACKET
    if (planned['JACKET']) {
      setShowJacketSection(true);
      setJacketFormData({
        ...initialFormData,
        design_no: po.design_no,
        garment_part: 'JACKET',
        received_date: today,
        challan_date: formData.challan_date || today,
        ...mapSizes(planned['JACKET']),
      });
      setSelectedJacketId(undefined);
    } else {
      setShowJacketSection(false);
      setJacketFormData(null);
    }
  };

  // Handler for design selection from dropdown
  const handleDesignChange = (_event: React.SyntheticEvent, design: Design | null) => {
    setSelectedDesign(design);
    
    if (design) {
      // Update form data with design info
      setFormData(prev => ({
        ...prev,
        design_no: design.design_no || '',
        image_url: design.image_url || '',
        received_date: prev.received_date, // Keep the existing date
        challan_date: prev.challan_date, // Keep the existing date
      }));
      
      // Update image preview with the design image
      setImagePreview(design.image_url || null);
      
      // Check if existing design cuttings with this design_no already exist
      const existingTopDesign = designs.find(d => d.design_no === design.design_no && d.garment_part === 'TOP');
      const existingBottomDesign = designs.find(d => d.design_no === design.design_no && d.garment_part === 'BOTTOM');
      
      // Initialize TOP form data and toggle based on existing data
      if (existingTopDesign) {
        setTopFormData({
          ...initialFormData,
          design_no: design.design_no || '',
          garment_part: 'TOP',
          received_date: existingTopDesign.received_date.split('T')[0],
          challan_date: existingTopDesign.challan_date ? existingTopDesign.challan_date.split('T')[0] : formData.challan_date,
          // Copy all other properties from existing design
          fabric_meter: existingTopDesign.fabric_meter,
          inner_fabric_meter: existingTopDesign.inner_fabric_meter || undefined,
          inner_received_date: existingTopDesign.inner_received_date ? existingTopDesign.inner_received_date.split('T')[0] : undefined,
          xs_pieces: existingTopDesign.xs_pieces,
          s_pieces: existingTopDesign.s_pieces,
          m_pieces: existingTopDesign.m_pieces,
          l_pieces: existingTopDesign.l_pieces,
          xl_pieces: existingTopDesign.xl_pieces,
          xxl_pieces: existingTopDesign.xxl_pieces,
          three_xl_pieces: existingTopDesign.three_xl_pieces,
          cutting_date: existingTopDesign.cutting_date ? existingTopDesign.cutting_date.split('T')[0] : undefined,
          rate: existingTopDesign.rate || undefined,
          notes: existingTopDesign.notes || undefined,
        });
        setShowTopSection(true);
        setSelectedTopId(existingTopDesign.id);
      } else {
        // Initialize new TOP form data
        setTopFormData({
          ...initialFormData,
          design_no: design.design_no || '',
          garment_part: 'TOP',
          received_date: new Date().toISOString().split('T')[0],
          challan_date: formData.challan_date,
        });
        // By default, enable TOP section for new designs unless BOTTOM already exists
        setShowTopSection(!existingBottomDesign);
        setSelectedTopId(undefined);
      }
      
      // Initialize BOTTOM form data and toggle based on existing data
      if (existingBottomDesign) {
        setBottomFormData({
          ...initialFormData,
          design_no: design.design_no || '',
          garment_part: 'BOTTOM',
          received_date: existingBottomDesign.received_date.split('T')[0],
          challan_date: existingBottomDesign.challan_date ? existingBottomDesign.challan_date.split('T')[0] : formData.challan_date,
          // Copy all other properties from existing design
          fabric_meter: existingBottomDesign.fabric_meter,
          inner_fabric_meter: existingBottomDesign.inner_fabric_meter || undefined,
          inner_received_date: existingBottomDesign.inner_received_date ? existingBottomDesign.inner_received_date.split('T')[0] : undefined,
          xs_pieces: existingBottomDesign.xs_pieces,
          s_pieces: existingBottomDesign.s_pieces,
          m_pieces: existingBottomDesign.m_pieces,
          l_pieces: existingBottomDesign.l_pieces,
          xl_pieces: existingBottomDesign.xl_pieces,
          xxl_pieces: existingBottomDesign.xxl_pieces,
          three_xl_pieces: existingBottomDesign.three_xl_pieces,
          cutting_date: existingBottomDesign.cutting_date ? existingBottomDesign.cutting_date.split('T')[0] : undefined,
          rate: existingBottomDesign.rate || undefined,
          notes: existingBottomDesign.notes || undefined,
        });
        setShowBottomSection(true);
        setSelectedBottomId(existingBottomDesign.id);
      } else {
        // Initialize new BOTTOM form data (but don't show it by default unless TOP doesn't exist)
        setBottomFormData({
          ...initialFormData,
          design_no: design.design_no || '',
          garment_part: 'BOTTOM',
          received_date: new Date().toISOString().split('T')[0],
          challan_date: formData.challan_date,
        });
        // By default, only enable BOTTOM section if TOP doesn't exist
        setShowBottomSection(!existingTopDesign);
        setSelectedBottomId(undefined);
      }
      
      // Ensure at least one section is shown
      if (!existingTopDesign && !existingBottomDesign) {
        // For new designs with no existing data, show TOP by default
        setShowTopSection(true);
        setShowBottomSection(false);
      }
      
      if (existingTopDesign || existingBottomDesign) {
        // Show warning that design cuttings already exist
        setError(`Note: This design (${design.design_no}) already has ${
          existingTopDesign && existingBottomDesign ? 'TOP and BOTTOM' 
          : existingTopDesign ? 'TOP' 
          : 'BOTTOM'
        } cutting records. Creating a new record may result in duplication.`);
      } else {
        // Clear any existing errors
        setError(null);

        // Prefill from job work if FULL_SET (create flow)
        prefillFromJobWorkIfFullSet(design.design_no);
      }
    } else {
      // Clear image preview if no design is selected
      setImagePreview(null);
      // Reset to default settings: TOP enabled, BOTTOM disabled
      setShowTopSection(true);
      setShowBottomSection(false);
    }
  };



  return (
    <Box 
      sx={{ 
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch',
        overflowX: 'hidden',
        flexGrow: 1
      }}
    >
      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs value={currentTab} onChange={(e, newValue) => setCurrentTab(newValue)}>
          <Tab label={`Pending to Receive (${pendingToReceive.length})`} />
          <Tab label={`Pending to Cut Designs (${filteredDesigns.length})`} />
          <Tab label={`Stitching In-Process (${completedDesigns.length})`} />
          <Tab label={`Stitching Complete (${dispatchedDesigns.length})`} />
        </Tabs>
      </Box>

      {/* Pending to Receive Tab - ISSUED jobs */}
      {currentTab === 0 && (
        <Paper elevation={1} sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Jobs Issued to Stitching Unit - Ready to Receive
          </Typography>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : pendingToReceive.length === 0 ? (
            <Box sx={{ py: 4, textAlign: 'center' }}>
              <Typography variant="body1" color="text.secondary">
                No pending jobs to receive at stitching unit
              </Typography>
            </Box>
          ) : (
            <Grid container spacing={2}>
              {pendingToReceive.map((jobWork) => {
                const imageUrl = jobWork.design?.image_url || jobWork.design?.image2_url || 
                                jobWork.design?.image3_url || jobWork.design?.image4_url;
                return (
                  <Grid item xs={12} sm={6} md={4} lg={3} key={jobWork.id}>
                    <Paper 
                      elevation={3} 
                      sx={{ 
                        borderRadius: 2,
                        overflow: 'hidden',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          transform: 'translateY(-4px)',
                          boxShadow: 6
                        },
                        cursor: 'pointer',
                        height: 400,
                        position: 'relative'
                      }}
                      onClick={() => {
                        setSelectedJobWork(jobWork);
                        setReceiveDialogOpen(true);
                      }}
                    >
                      {/* Image - Full Card Background */}
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: '100%',
                          height: '100%',
                          bgcolor: 'background.default',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
                        {imageUrl ? (
                          <Box
                            component="img"
                            src={imageUrl}
                            alt={jobWork.designNo}
                            sx={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover'
                            }}
                          />
                        ) : (
                          <Avatar
                            variant="rounded"
                            sx={{
                              width: '100%',
                              height: '100%',
                              bgcolor: 'grey.200',
                              fontSize: '3rem',
                              borderRadius: 0
                            }}
                          >
                            {jobWork.designNo.substring(0, 2)}
                          </Avatar>
                        )}
                      </Box>

                      {/* Gradient Overlay for Text Readability */}
                      <Box
                        sx={{
                          position: 'absolute',
                          bottom: 0,
                          left: 0,
                          right: 0,
                          background: 'linear-gradient(to top, rgba(0,0,0,0.8) 0%, rgba(0,0,0,0.6) 50%, rgba(0,0,0,0) 100%)',
                          p: 2,
                          pt: 4
                        }}
                      >
                        <Typography variant="h6" fontWeight="bold" sx={{ color: 'white', mb: 1 }}>
                          {jobWork.jobWorkCode}
                        </Typography>
                        
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)' }}>
                              Design:
                            </Typography>
                            <Typography variant="body2" fontWeight="medium" sx={{ color: 'white' }}>
                              {jobWork.designNo}
                            </Typography>
                          </Box>
                          
                          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)' }}>
                              Vendor:
                            </Typography>
                            <Typography variant="body2" fontWeight="medium" sx={{ color: 'white' }}>
                              {jobWork.vendor?.alias || jobWork.vendor?.registered_name || 'N/A'}
                            </Typography>
                          </Box>
                          
                          {jobWork.issueDate && (
                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)' }}>
                                Issued:
                              </Typography>
                              <Typography variant="body2" fontWeight="medium" sx={{ color: 'white' }}>
                                {new Date(jobWork.issueDate).toLocaleDateString()}
                              </Typography>
                            </Box>
                          )}
                        </Box>

                        {/* Footer Button */}
                        <Box sx={{ mt: 2 }}>
                          <Button
                            variant="contained"
                            size="small"
                            fullWidth
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedJobWork(jobWork);
                              setReceiveDialogOpen(true);
                            }}
                            sx={{
                              fontWeight: 600,
                              textTransform: 'none'
                            }}
                          >
                            Receive at Stitching
                          </Button>
                        </Box>
                      </Box>
                    </Paper>
                  </Grid>
                );
              })}
            </Grid>
          )}
        </Paper>
      )}

      {/* Pending to Cut Designs Tab - Card View */}
      {currentTab === 1 && (
        <Paper elevation={1} sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h6" fontWeight="bold">
              Designs Ready for Cutting Entry
            </Typography>
            {canAdd && (
              <Button 
                variant="contained" 
                startIcon={<AddIcon />}
                onClick={handleOpen}
                sx={{ 
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 600
                }}
              >
                Add Design
              </Button>
            )}
          </Box>

          {/* Search and Toggle */}
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 3, flexWrap: 'wrap' }}>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Search by design number or garment part..."
              size="medium"
              value={searchQuery}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                )
              }}
              onChange={(e) => setSearchQuery(e.target.value)}
              sx={{ flex: 1, minWidth: 250 }}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={!showPendingOnly}
                  onChange={(e) => setShowPendingOnly(!e.target.checked)}
                  color="primary"
                />
              }
              label="Show Completed Cutting Records"
            />
          </Box>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : filteredDesigns.length === 0 ? (
            <Box sx={{ py: 4, textAlign: 'center' }}>
              <Typography variant="body1" color="text.secondary">
                No designs pending to cut
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                All designs have been processed!
              </Typography>
            </Box>
          ) : (
            <Grid container spacing={3}>
              {filteredDesigns.map((design) => {
                const imageUrl = (design as any).design?.image_url || (design as any).design?.image2_url || 
                                (design as any).design?.image3_url || (design as any).design?.image4_url || 
                                design.image_url;
                return (
                  <Grid item xs={12} sm={6} md={4} lg={3} key={design.id}>
                    <Paper 
                      elevation={3} 
                      sx={{ 
                        borderRadius: 2,
                        overflow: 'hidden',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          transform: 'translateY(-4px)',
                          boxShadow: 6
                        },
                        cursor: 'pointer',
                        height: 400,
                        position: 'relative'
                      }}
                      onClick={() => {
                        handleEdit(design);
                      }}
                    >
                      {/* Image - Full Card Background */}
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: '100%',
                          height: '100%',
                          bgcolor: 'background.default',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
                        {imageUrl ? (
                          <Box
                            component="img"
                            src={imageUrl}
                            alt={design.design_no}
                            sx={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover'
                            }}
                          />
                        ) : (
                          <Avatar
                            variant="rounded"
                            sx={{
                              width: '100%',
                              height: '100%',
                              bgcolor: 'grey.200',
                              fontSize: '3rem',
                              borderRadius: 0
                            }}
                          >
                            {design.design_no.substring(0, 2)}
                          </Avatar>
                        )}
                      </Box>

                      {/* Delete Button - Top Right - Only show if no worker assignments */}
                      {canDelete && !recordsWithAssignments.has(design.id) && (
                        <IconButton
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(design.id);
                          }}
                          sx={{
                            position: 'absolute',
                            top: 8,
                            right: 8,
                            bgcolor: 'rgba(0,0,0,0.5)',
                            color: 'white',
                            '&:hover': {
                              bgcolor: 'error.main',
                              color: 'white'
                            },
                            zIndex: 10
                          }}
                          size="small"
                        >
                          <DeleteIcon />
                        </IconButton>
                      )}

                      {/* Gradient Overlay for Text Readability */}
                      <Box
                        sx={{
                          position: 'absolute',
                          bottom: 0,
                          left: 0,
                          right: 0,
                          background: 'linear-gradient(to top, rgba(0,0,0,0.8) 0%, rgba(0,0,0,0.6) 50%, rgba(0,0,0,0) 100%)',
                          p: 2,
                          pt: 4
                        }}
                      >
                        <Typography variant="h6" fontWeight="bold" sx={{ color: 'white', mb: 1 }}>
                          {design.design_no}
                        </Typography>
                        
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)' }}>
                              Garment Part:
                            </Typography>
                            <Typography variant="body2" fontWeight="medium" sx={{ color: 'white' }}>
                              {design.garment_part}
                            </Typography>
                          </Box>
                          {/* Per-part cut status */}
                          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 0.5 }}>
                            {(() => {
                              const top = designs.find(d => d.design_no === (design as any).design_no && d.garment_part === 'TOP');
                              const bottom = designs.find(d => d.design_no === (design as any).design_no && d.garment_part === 'BOTTOM');
                              const topCut = top && top.status === 'COMPLETED';
                              const bottomCut = bottom && bottom.status === 'COMPLETED';
                              return (
                                <>
                                  <Chip label={`TOP: ${topCut ? 'Cut' : 'Pending Cut'}`} size="small" color={topCut ? 'success' : 'warning'} variant={topCut ? 'filled' : 'outlined'} />
                                  <Chip label={`BOTTOM: ${bottomCut ? 'Cut' : 'Pending Cut'}`} size="small" color={bottomCut ? 'success' : 'warning'} variant={bottomCut ? 'filled' : 'outlined'} />
                                </>
                              );
                            })()}
                          </Box>
                          
                          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)' }}>
                              Batch:
                            </Typography>
                            <Typography variant="body2" fontWeight="medium" sx={{ color: 'white' }}>
                              #{design.batch_number}
                            </Typography>
                          </Box>
                          
                          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)' }}>
                              Received:
                            </Typography>
                            <Typography variant="body2" fontWeight="medium" sx={{ color: 'white' }}>
                              {new Date(design.received_date).toLocaleDateString()}
                            </Typography>
                          </Box>

                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)' }}>
                              Status:
                            </Typography>
                            <Chip 
                              label="Pending" 
                              color="warning" 
                              size="small"
                              sx={{ fontWeight: 600 }}
                            />
                          </Box>
                        </Box>

                        {/* Footer Text */}
                        <Box sx={{ mt: 2, textAlign: 'center' }}>
                          <Typography variant="body2" fontWeight="bold" sx={{ color: 'primary.light' }}>
                            Click to Enter Cutting Details
                          </Typography>
                        </Box>
                      </Box>
                    </Paper>
                  </Grid>
                );
              })}
            </Grid>
          )}
        </Paper>
      )}

      {/* Stitching In-Process Tab - Card View */}
      {currentTab === 2 && (
        <Paper elevation={1} sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, gap: 2, flexWrap: 'wrap' }}>
            <Typography variant="h6" fontWeight="bold">
              Stitching In-Process - Pending to Complete
            </Typography>
            <TextField
              size="small"
              placeholder="Search design no..."
              value={inProcessSearch}
              onChange={(e) => setInProcessSearch(e.target.value)}
            />
          </Box>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : completedDesigns.length === 0 ? (
            <Box sx={{ py: 4, textAlign: 'center' }}>
              <Typography variant="body1" color="text.secondary">
                No completed designs found
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Designs will appear here after cutting is completed
              </Typography>
            </Box>
          ) : (
            <Grid container spacing={3}>
              {completedDesigns
                .filter((d: any) => (d.design_no || '').toLowerCase().includes(inProcessSearch.toLowerCase()))
                .map((design) => {
                const imageUrl = (design as any).design?.image_url || (design as any).design?.image2_url || 
                                (design as any).design?.image3_url || (design as any).design?.image4_url || 
                                design.image_url;
                // Use pre-calculated total from all garment parts
                const totalPieces = (design as any).totalCutPieces || 
                                   ((design.xs_pieces || 0) + (design.s_pieces || 0) + 
                                   (design.m_pieces || 0) + (design.l_pieces || 0) + 
                                   (design.xl_pieces || 0) + (design.xxl_pieces || 0) + 
                                   (design.three_xl_pieces || 0));
                
                // Calculate days since job issued
                const calculateDays = (dateString: string) => {
                  if (!dateString) return 0;
                  const date = new Date(dateString);
                  const now = new Date();
                  const diffTime = Math.abs(now.getTime() - date.getTime());
                  return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
                };
                
                const jobWork = (design as any).jobWork;
                
                // Try multiple possible field names and formats
                // Priority: jobWork issueDate > jobWork createdAt > design cutting_date
                const issueDate = jobWork?.issueDate || jobWork?.issue_date || 
                                 jobWork?.created_at || jobWork?.createdAt ||
                                 design.cutting_date;
                
                const daysSinceJobIssued = issueDate 
                  ? calculateDays(issueDate) 
                  : 0;
                const pendingQty = (design as any).pendingToComplete || 0;
                
                return (
                  <Grid item xs={12} sm={6} md={4} lg={3} key={design.id}>
                    <Paper 
                      elevation={3} 
                      sx={{ 
                        borderRadius: 2,
                        overflow: 'hidden',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          transform: 'translateY(-4px)',
                          boxShadow: 6
                        },
                        cursor: 'pointer',
                        height: 400,
                        position: 'relative'
                      }}
                      onClick={() => {
                        // Use the first garment part from allParts array
                        const firstPart = (design as any).allParts ? (design as any).allParts[0] : design;
                        setSelectedCuttingForCompletion(firstPart);
                        setStitchingDialogOpen(true);
                        // Note: viewOnly will be passed to dialog based on currentTab === 2
                      }}
                    >
                      {/* Image - Full Card Background */}
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: '100%',
                          height: '100%',
                          bgcolor: 'background.default',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
                        {imageUrl ? (
                          <Box
                            component="img"
                            src={imageUrl}
                            alt={design.design_no}
                            sx={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover'
                            }}
                          />
                        ) : (
                          <Avatar
                            variant="rounded"
                            sx={{
                              width: '100%',
                              height: '100%',
                              bgcolor: 'grey.200',
                              fontSize: '3rem',
                              borderRadius: 0
                            }}
                          >
                            {design.design_no.substring(0, 2)}
                          </Avatar>
                        )}
                      </Box>

                      {/* Gradient Overlay for Text Readability */}
                      <Box
                        sx={{
                          position: 'absolute',
                          bottom: 0,
                          left: 0,
                          right: 0,
                          background: 'linear-gradient(to top, rgba(0,0,0,0.8) 0%, rgba(0,0,0,0.6) 50%, rgba(0,0,0,0) 100%)',
                          p: 2,
                          pt: 4
                        }}
                      >
                        <Typography variant="h6" fontWeight="bold" sx={{ color: 'white', mb: 1 }}>
                          {design.design_no}
                        </Typography>
                        
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)' }}>
                              Garment Part:
                            </Typography>
                            <Typography variant="body2" fontWeight="medium" sx={{ color: 'white' }}>
                              {design.garment_part}
                            </Typography>
                          </Box>
                          
                          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)' }}>
                              Stitching Timeline:
                            </Typography>
                            <Typography variant="body2" fontWeight="bold" sx={{ color: 'primary.light' }}>
                              {jobWork ? `${daysSinceJobIssued} days` : 'Not Issued'}
                            </Typography>
                          </Box>
                          
                          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)' }}>
                              Total Cut:
                            </Typography>
                            <Typography variant="body2" fontWeight="medium" sx={{ color: 'white' }}>
                              {totalPieces}
                            </Typography>
                          </Box>

                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)' }}>
                              Pending to Complete:
                            </Typography>
                            <Chip 
                              label={`${pendingQty} pcs`}
                              color={pendingQty > 0 ? "warning" : "success"}
                              size="small"
                              sx={{ fontWeight: 600 }}
                            />
                          </Box>
                        </Box>

                        {/* Footer Text */}
                        <Box sx={{ mt: 2, textAlign: 'center' }}>
                          <Typography variant="body2" fontWeight="bold" sx={{ color: 'info.light' }}>
                            Click to View Stitching Status
                          </Typography>
                        </Box>
                      </Box>
                    </Paper>
                  </Grid>
                );
              })}
            </Grid>
          )}
        </Paper>
      )}

      {/* Stitching Complete Tab - Card View */}
      {currentTab === 3 && (
        <Paper elevation={1} sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, gap: 2, flexWrap: 'wrap' }}>
            <Typography variant="h6" fontWeight="bold">
              Stitching Complete
            </Typography>
            <TextField
              size="small"
              placeholder="Search design no..."
              value={dispatchedSearch}
              onChange={(e) => setDispatchedSearch(e.target.value)}
            />
          </Box>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : dispatchedDesigns.length === 0 ? (
            <Box sx={{ py: 4, textAlign: 'center' }}>
              <Typography variant="body1" color="text.secondary">
                No designs dispatched yet
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Designs will appear here after stitching is fully completed
              </Typography>
            </Box>
          ) : (
            <Grid container spacing={3}>
              {dispatchedDesigns
                .filter((d: any) => (d.design_no || '').toLowerCase().includes(dispatchedSearch.toLowerCase()))
                .map((design) => {
                const imageUrl = (design as any).design?.image_url || (design as any).design?.image2_url || 
                                (design as any).design?.image3_url || (design as any).design?.image4_url || 
                                design.image_url;
                // Use pre-calculated total from all garment parts
                const totalPieces = (design as any).totalCutPieces || 
                                   ((design.xs_pieces || 0) + (design.s_pieces || 0) + 
                                   (design.m_pieces || 0) + (design.l_pieces || 0) + 
                                   (design.xl_pieces || 0) + (design.xxl_pieces || 0) + 
                                   (design.three_xl_pieces || 0));
                
                return (
                  <Grid item xs={12} sm={6} md={4} lg={3} key={design.id}>
                      <Paper 
                      elevation={3} 
                      sx={{ 
                        borderRadius: 2,
                        overflow: 'hidden',
                        border: '2px solid',
                        borderColor: 'success.main',
                        height: 400,
                        position: 'relative'
                      }}
                      onClick={() => {
                        setDetailsDesignNo(design.design_no);
                        setDetailsOpen(true);
                      }}
                    >
                      {/* Image - Full Card Background */}
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: '100%',
                          height: '100%',
                          bgcolor: 'background.default',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
                        {imageUrl ? (
                          <Box
                            component="img"
                            src={imageUrl}
                            alt={design.design_no}
                            sx={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover'
                            }}
                          />
                        ) : (
                          <Avatar
                            variant="rounded"
                            sx={{
                              width: '100%',
                              height: '100%',
                              bgcolor: 'grey.200',
                              fontSize: '3rem',
                              borderRadius: 0
                            }}
                          >
                            {design.design_no.substring(0, 2)}
                          </Avatar>
                        )}
                      </Box>

                      {/* Gradient Overlay for Text Readability */}
                      <Box
                        sx={{
                          position: 'absolute',
                          bottom: 0,
                          left: 0,
                          right: 0,
                          background: 'linear-gradient(to top, rgba(0,0,0,0.8) 0%, rgba(0,0,0,0.6) 50%, rgba(0,0,0,0) 100%)',
                          p: 2,
                          pt: 4
                        }}
                      >
                        <Typography variant="h6" fontWeight="bold" sx={{ color: 'white', mb: 1 }}>
                          {design.design_no}
                        </Typography>
                        
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)' }}>
                              Garment Part:
                            </Typography>
                            <Typography variant="body2" fontWeight="medium" sx={{ color: 'white' }}>
                              {design.garment_part}
                            </Typography>
                          </Box>
                          
                          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)' }}>
                              Total Pieces:
                            </Typography>
                            <Typography variant="body2" fontWeight="medium" sx={{ color: 'white' }}>
                              {totalPieces}
                            </Typography>
                          </Box>

                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)' }}>
                              Status:
                            </Typography>
                            <Chip 
                              label="Dispatched"
                              color="success"
                              size="small"
                              sx={{ fontWeight: 600 }}
                            />
                          </Box>
                        </Box>
                      </Box>

                    </Paper>
                  </Grid>
                );
              })}
            </Grid>
          )}
        </Paper>
      )}

      {/* Receive at Stitching Dialog */}
      <DispatchedDetailsDialog
        open={detailsOpen}
        designNo={detailsDesignNo}
        onOpenCuttingForm={(dn, ro) => {
          openFormForDesignNo(dn, ro);
        }}
        onClose={() => {
          setDetailsOpen(false);
          setDetailsDesignNo(null);
        }}
      />

      <Dialog
        open={receiveDialogOpen}
        onClose={() => {
          setReceiveDialogOpen(false);
          setSelectedJobWork(null);
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Receive Job at Stitching Unit</DialogTitle>
        <DialogContent>
          {selectedJobWork && (
            <Box sx={{ pt: 2 }}>
              <Grid container spacing={2}>
                {/* Job Work Details */}
                <Grid item xs={12}>
                  <Paper variant="outlined" sx={{ p: 2, bgcolor: 'background.default' }}>
                    <Typography variant="h6" gutterBottom color="primary">
                      Job Work Details
                    </Typography>
                    <Grid container spacing={1}>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">Job Work Code</Typography>
                        <Typography variant="body1" fontWeight="bold">{selectedJobWork.jobWorkCode}</Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">Design No</Typography>
                        <Typography variant="body1" fontWeight="bold">{selectedJobWork.designNo}</Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">Vendor</Typography>
                        <Typography variant="body1">{selectedJobWork.vendor?.alias || selectedJobWork.vendor?.registered_name}</Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">Issue Date</Typography>
                        <Typography variant="body1">
                          {selectedJobWork.issueDate ? new Date(selectedJobWork.issueDate).toLocaleDateString('en-IN') : '-'}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Paper>
                </Grid>

                {/* Design Details - Pieces */}
                {selectedJobWork.designDetails && selectedJobWork.designDetails.length > 0 && (
                  <Grid item xs={12}>
                    <Paper variant="outlined" sx={{ p: 2 }}>
                      <Typography variant="h6" gutterBottom color="primary">
                        Pieces Issued
                      </Typography>
                      <TableContainer>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell><strong>Garment Part</strong></TableCell>
                              <TableCell><strong>Size</strong></TableCell>
                              <TableCell align="right"><strong>Pieces</strong></TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {selectedJobWork.designDetails.map((detail: any, index: number) => (
                              <TableRow key={index}>
                                <TableCell>{detail.garmentPart}</TableCell>
                                <TableCell>{detail.size}</TableCell>
                                <TableCell align="right"><strong>{detail.pieces}</strong></TableCell>
                              </TableRow>
                            ))}
                            <TableRow sx={{ bgcolor: 'action.hover' }}>
                              <TableCell colSpan={2}><strong>Total Pieces</strong></TableCell>
                              <TableCell align="right">
                                <strong>
                                  {selectedJobWork.designDetails.reduce((sum: number, d: any) => sum + d.pieces, 0)}
                                </strong>
                              </TableCell>
                            </TableRow>
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Paper>
                  </Grid>
                )}

                {/* Materials Issued */}
                {selectedJobWork.materials && selectedJobWork.materials.length > 0 && (
                  <Grid item xs={12}>
                    <Paper variant="outlined" sx={{ p: 2 }}>
                      <Typography variant="h6" gutterBottom color="primary">
                        Materials Issued
                      </Typography>
                      <TableContainer>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell><strong>Material</strong></TableCell>
                              <TableCell><strong>Type</strong></TableCell>
                              <TableCell align="right"><strong>Quantity</strong></TableCell>
                              <TableCell><strong>UOM</strong></TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {selectedJobWork.materials.map((mat: any, index: number) => (
                              <TableRow key={index}>
                                <TableCell>{mat.material?.material_code || '-'}</TableCell>
                                <TableCell>{mat.material?.material_type || '-'}</TableCell>
                                <TableCell align="right"><strong>{mat.quantity}</strong></TableCell>
                                <TableCell>{mat.uom}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Paper>
                  </Grid>
                )}

                {/* Program Information */}
                {selectedJobWork.notes && (
                  <Grid item xs={12}>
                    <Paper variant="outlined" sx={{ p: 2, bgcolor: 'info.light' }}>
                      <Typography variant="h6" gutterBottom color="info.dark">
                        Program Information / Notes
                      </Typography>
                      <Typography variant="body2">{selectedJobWork.notes}</Typography>
                    </Paper>
                  </Grid>
                )}

                {/* Challan Date */}
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Challan Date"
                    type="date"
                    value={challanDate}
                    onChange={(e) => setChallanDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Alert severity="info">
                    This will create design cutting records for all garment parts and mark the job as received at the stitching unit.
                  </Alert>
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setReceiveDialogOpen(false);
              setSelectedJobWork(null);
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleReceiveAtStitching}
            disabled={loading}
          >
            {loading ? 'Receiving...' : 'Confirm & Receive'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success/Error Messages */}
      <Snackbar
        open={Boolean(successMessage)}
        autoHideDuration={6000}
        onClose={() => setSuccessMessage(null)}
      >
        <Alert severity="success" onClose={() => setSuccessMessage(null)}>
          {successMessage}
        </Alert>
      </Snackbar>

      <Snackbar
        open={Boolean(error)}
        autoHideDuration={6000}
        onClose={() => setError(null)}
      >
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      </Snackbar>

      {/* Form Dialog */}
      <Dialog 
        open={open} 
        onClose={handleClose}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {editMode ? 'Edit Design' : selectedDesign ? `Create New Batch - ${selectedDesign.design_no}` : 'Add New Design'}
        </DialogTitle>
        
        <DialogContent dividers>
          {renderFormContent()}
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained" color="primary" disabled={formReadOnly}>
            {editMode ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Use new DesignPreviewDialog component */}
      <DesignPreviewDialog
        open={previewDialogOpen}
        onClose={closeImagePreview}
        design={selectedDesign}
        previewImage={previewImage}
        designNo={selectedPreviewDesignNo}
        topDesign={selectedPreviewTopDesign}
        bottomDesign={selectedPreviewBottomDesign}
      />

      {/* Stitching Completion Dialog */}
      <StitchingCompletionDialog
        open={stitchingDialogOpen}
        designCutting={selectedCuttingForCompletion}
        onClose={() => {
          setStitchingDialogOpen(false);
          setSelectedCuttingForCompletion(null);
        }}
        onComplete={() => {
          fetchDesigns();
          fetchCompletedDesigns();
          // Run a second refresh shortly after to account for any backend consolidation delays
          setTimeout(() => {
            fetchCompletedDesigns();
          }, 1200);
        }}
        viewOnly={currentTab === 2 || currentTab === 3}
        onOpenCuttingForm={(dn, ro) => {
          openFormForDesignNo(dn, ro);
        }}
      />
    </Box>
  );
} 